import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useEditor, EditorContent, Extension } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Table as TableExtension } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'
import { ImageResize } from 'tiptap-extension-resize-image'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Link,
  Table,
  Loader2,
  Check,
  X,
  Undo2,
  Redo2,
  Download,
  AlertTriangle,
  RefreshCw,
  Lock,
  CheckCircle2,
  FileCheck,
  Send,
  MessageSquare,
  ShieldCheck,
  RotateCcw,
  ArrowLeft,
  History,
  Image as ImageIcon,
  Eye,
  MoreHorizontal,
} from 'lucide-react'
import type { OutlineSection, EditSelectionResponse, DraftSegment, SegmentConflictDetails } from '@/lib/api'
import type { CollaborationStatus } from '@/stores/section-assignments-store'
import { saveSegment, backupSegmentContent, exportArtifactToDocx, ApiRequestError, toastApiError } from '@/lib/api'
import { SegmentConflictDialog } from './SegmentConflictDialog'
import { createSaveQueue } from '@/components/drafting/saveQueue'
import { createUnconfirmedSaveLog, saveAdoptingOwnWrite } from '@/components/drafting/unconfirmedSaves'
import { VersionHistoryDialog } from './VersionHistoryDialog'
import { ExportGapConfirmDialog } from './ExportGapConfirmDialog'
import { scanContentForGaps, type LocalGapScan } from '@/lib/exportGaps'
import { uploadEditorImage, EditorImageUploadError } from '@/lib/editor-image-upload'
import { useEditingPresence } from '@/hooks/useEditingPresence'
import { useAgentStore, type SelectionState } from '@/stores/agent-store'
import { useBidReadOnly, useIsViewOnly } from '@/hooks/useViewOnly'
import { DOCUMENT_SECTION_KEY } from '@/types/scope'
import { cn } from '@/lib/utils'
import { NOTICE_ACT } from '@/lib/notice-tier'
import { getInitials, getAvatarColor } from '@/lib/avatar'
import { toast } from 'sonner'
import { toastExportResult } from '@/lib/pending-ai-change-notices'
import {
  assembleEditorDocument,
  countWords,
  detectChangedSegments,
  extractSegmentsFromEditor,
  getSelectionSegmentId,
  isSelectionCrossSegment,
  normalizeRichTextForEditor,
} from './segmentAssembly'
import { SegmentHeading } from './SegmentHeading'
import { segmentStatusPlugin } from './segmentStatusPlugin'
import { createCitationDecorationPlugin } from './citationDecorationPlugin'
import { createAnnotationDecorationPlugin } from '@/components/drafting/annotationDecorationPlugin'
import { createCommentDecorationPlugin } from './commentDecorationPlugin'
import { useCommentDecorationFeed } from '@/hooks/useCommentDecorationFeed'
import { CitationTooltip, type CitationTooltipState } from './CitationTooltip'
import { DraftStatsFooter } from '@/components/drafting/DraftStatsFooter'
import type { CitedSource, DraftToDosStat } from '@/lib/draft-stats'
import { CommentBubbleButton } from './CommentBubbleButton'
import { SectionStatusPill } from './SectionStatusPill'
import {
  findSectionRange,
  resolveAnchor,
  type AnchorDocSource,
  type AnchorRange,
} from '@/lib/commentAnchor'
import { useCommentsStore } from '@/stores/comments-store'
import { useUnifiedAiPanelStore } from '@/stores/unified-ai-panel-store'
import { diffMarks } from './diffMarks'
import { buildSegmentDiff, countDiffWords, type DiffHunk } from './segmentDiff'
import { isStagedPreview, previewDiffBase, previewOwnRefs, withPreviewCitationRefs } from '@/lib/staged-suggestion'
import { SUGGESTION_DISMISS, SUGGESTION_OUTDATED } from '@/lib/ai-edit-copy'
import { EditorAppliedEditNotice } from '@/components/drafting/AppliedEditNotice'
import { JobSuggestionReviewNotes } from '@/components/drafting/SuggestionReviewNotes'

// ===================
// Types
// ===================

interface BidMemberInfo {
  userId: string
  fullName: string | null
  avatarUrl: string | null
  email: string
  role: string
}

interface ReviewRoundInfo {
  reviewerId: string
  reviewerName?: string
  /** 'withdrawn' is written when the section leaves In Review without a decision. */
  decision: 'pending' | 'approved' | 'changes_requested' | 'withdrawn'
  comment: string | null
  createdAt: string
  decidedAt?: string
  /** Spec 29 §7.3: the writer cannot mark Complete until the section goes back to review. */
  requireReReview?: boolean
}

interface DraftEditorProps {
  artifactId: string
  /**
   * Enables the selection comment bubble without an agent session. Readers and
   * reviewers never bootstrap a Draft Agent session (A0), so the bubble must
   * not depend on sessionInitContext — that dependency silently removed
   * commenting for the exact audience it was built for.
   */
  bidId?: string
  sectionKey: string
  section: OutlineSection
  documentSectionNumber: number // 1-based position in the document outline
  canWrite?: boolean
  /** Who the section is assigned to, when that is why it is view-only. A name, never a sentence. */
  lockedByUser?: string | null
  /**
   * Why the whole document is view-only (finalised, under review), as a
   * sentence without its full stop. Wins over `lockedByUser` in the banner.
   */
  lockedReason?: string | null
  collaborationStatus?: CollaborationStatus | null
  onCollaborationStatusChange?: (status: CollaborationStatus) => void
  // Review flow props
  isReviewer?: boolean
  isOwner?: boolean
  writerName?: string | null
  bidMembers?: BidMemberInfo[]
  currentUserId?: string | undefined
  latestReviewRound?: ReviewRoundInfo | null | undefined
  /**
   * The reviewer planned from the outline table. Listed first in the
   * Submit/Request review menus and marked "Planned" — the writer still picks.
   */
  plannedReviewerId?: string | null | undefined
  onRequestReview?: (reviewerId: string) => void
  onSubmitReview?: (decision: 'approved' | 'changes_requested', comment?: string, options?: { requireReReview?: boolean }) => void
  placeholderText?: string
  /** When set, renders a back button + title inside the chrome row. */
  onBack?: () => void
  headerTitle?: string
  /** Optional slot rendered immediately below the toolbar (e.g., question prompt for questionnaires). */
  belowToolbarSlot?: React.ReactNode
  /** Optional slot rendered in the chrome header, immediately after the status pill (e.g., compact Brief button). */
  headerStatusSlot?: React.ReactNode
  /**
   * To-dos and sources for the footer stats line ("412 words · 3 to-dos ·
   * 8 sources"). Only the section editor passes it; without it the footer
   * shows the word count alone, as before.
   */
  footerStats?: {
    toDos: DraftToDosStat
    sources: CitedSource[]
  }
}

type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error' | 'conflict' | 'blocked'

interface ConflictInfo {
  sectionId: string
  localContent: string
  localWordCount: number
  remoteVersion: number
  remoteContent: string
  editorName: string | null
  /** The competing write is attributed to the current user (an AI write on their behalf). */
  isSelf: boolean
}

/**
 * Interpret a 409's `lastEditedBy` for the conflict dialog.
 *
 * The server attributes agent writes to the user who invoked them, so a conflict
 * naming the current user is the app's own AI write racing their typing — not a
 * colleague. Reporting it as a third party (and by the reader's own name) is what
 * made the single-user, single-tab conflict read as a phantom collaborator.
 */
export function resolveConflictAttribution(
  lastEditedBy: { id: string; fullName: string | null } | null | undefined,
  currentUserId: string | undefined
): { editorName: string | null; isSelf: boolean } {
  if (lastEditedBy === null || lastEditedBy === undefined) {
    return { editorName: null, isSelf: false }
  }
  if (currentUserId && lastEditedBy.id === currentUserId) {
    return { editorName: null, isSelf: true }
  }
  return { editorName: lastEditedBy.fullName ?? 'A teammate', isSelf: false }
}

function getWordCountColor(wordCount: number, target: number | undefined): string {
  if (!target) return 'text-muted-foreground'
  const ratio = wordCount / target
  if (ratio >= 1.1) return 'text-destructive'
  if (ratio >= 0.9) return 'text-[hsl(var(--status-warning))]'
  return 'text-muted-foreground'
}


function getNormalizedEditorHtml(editor: NonNullable<ReturnType<typeof useEditor>>): string {
  const text = editor.getText().replace(/\s+/g, ' ').trim()
  if (!text) return ''
  const html = editor.getHTML()
  return html === '<p></p>' ? '' : html
}

// ===================
// Autosave resilience (WF4)
// ===================

/**
 * Backoff schedule for auto-retrying a failed save. After these three attempts
 * the editor holds and surfaces a manual "Retry" action.
 */
export const RETRY_DELAYS = [2000, 5000, 15000]

/** How long after an edit autosave sends it. */
const AUTOSAVE_DEBOUNCE_MS = 500

/** A `beforeunload` guard is armed while the draft has unpersisted work. */
export function shouldGuardBeforeUnload(status: SaveStatus): boolean {
  return status === 'unsaved' || status === 'saving' || status === 'error'
}

export type AutosavePlan =
  | { kind: 'debounce'; delay: number }
  | { kind: 'retry'; delay: number; nextCount: number }
  | { kind: 'exhausted' }
  | { kind: 'idle' }

/**
 * Decide what the autosave timer should do next. Pure so the backoff sequence
 * is unit-testable independently of the editor.
 * - fresh edit (`unsaved`) → debounce a save (and reset the retry counter)
 * - failed save (`error`) → auto-retry on the backoff schedule until exhausted,
 *   then hold for a manual Retry
 * - a live conflict or blocked state suspends all auto-saving
 */
export function planAutosaveTimer(
  status: SaveStatus,
  retryCount: number,
  hasConflict: boolean,
  hasBlock: boolean
): AutosavePlan {
  if (hasConflict || hasBlock) return { kind: 'idle' }
  if (status === 'unsaved') return { kind: 'debounce', delay: AUTOSAVE_DEBOUNCE_MS }
  if (status === 'error') {
    const delay = RETRY_DELAYS[retryCount]
    if (delay !== undefined) {
      return { kind: 'retry', delay, nextCount: retryCount + 1 }
    }
    return { kind: 'exhausted' }
  }
  return { kind: 'idle' }
}

/**
 * On-failure local mirror of a segment's content. Lets a user recover work that
 * never reached the server (flaky save + tab close) on the next mount.
 */
export interface DraftMirror {
  content: string
  baseVersion: number
  savedAt: string
}

function draftMirrorKey(sessionId: string, sectionId: string): string {
  return `draft-mirror:${sessionId}:${sectionId}`
}

/** Persist a failed save locally. Quota-safe: a full/blocked store never throws. */
export function writeDraftMirror(sessionId: string, sectionId: string, entry: DraftMirror): void {
  try {
    localStorage.setItem(draftMirrorKey(sessionId, sectionId), JSON.stringify(entry))
  } catch {
    // localStorage unavailable (private mode) or over quota — the in-memory
    // dirty refs are still the primary safety net; the mirror is best-effort.
  }
}

/** Read a previously mirrored save, or null if none / unreadable. */
export function readDraftMirror(sessionId: string, sectionId: string): DraftMirror | null {
  try {
    const raw = localStorage.getItem(draftMirrorKey(sessionId, sectionId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DraftMirror>
    if (typeof parsed.content !== 'string') return null
    return {
      content: parsed.content,
      baseVersion: typeof parsed.baseVersion === 'number' ? parsed.baseVersion : 0,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
    }
  } catch {
    return null
  }
}

/** Clear the mirror after a successful save or an explicit discard. */
export function clearDraftMirror(sessionId: string, sectionId: string): void {
  try {
    localStorage.removeItem(draftMirrorKey(sessionId, sectionId))
  } catch {
    // Best-effort — see writeDraftMirror.
  }
}

/**
 * Scroll a hunk of the active diff preview into view, addressed by its id.
 *
 * `buildSegmentDiff` stamps `data-hunk-id` on every root element of each hunk's
 * diff fragment and the `hunkIdAttribute` extension round-trips it through
 * TipTap, so the lookup is exact — unlike the old index counting, which
 * misaligned whenever a hunk rendered more than one changed DOM block (void
 * element diffs emit sibling `<del>`/`<ins>`).
 */
function scrollToHunkInEditor(hunkId: string): void {
  const root = document.querySelector('.ProseMirror') ?? document.querySelector('[contenteditable="true"]')
  const target = root?.querySelector(`[data-hunk-id="${CSS.escape(hunkId)}"]`)
  target?.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

// ===================
// Comment jump (C3 rows 1 and 2)
// ===================

/** Anchor fields the `comment:jump` event carries. */
export interface CommentJumpDetail {
  commentId: string
  sectionId: string | null
  startPos: number | null
  endPos: number | null
  quotedText: string | null
}

/**
 * The slice of a ProseMirror node the rewrite scan reads. Structural so the
 * scan is testable without a real document.
 */
export interface RewriteScanNode {
  isTextblock?: boolean
  content: { size: number }
  textBetween: (from: number, to: number, blockSeparator?: string) => string
  descendants?: (f: (node: RewriteScanNode, pos: number) => boolean | void) => void
}

/**
 * Where an AI selection rewrite should actually land.
 *
 * The range was captured at selection time, in absolute document positions. By
 * the time the writer clicks Accept the document may have moved — an agent write
 * lands, a segment refresh reassembles the composite document — and applying the
 * old offsets pastes the rewrite over unrelated prose, deleting what was there.
 *
 * So: use the stored range only while the text at it still matches. Otherwise
 * re-locate the exact text, and only when it occurs ONCE — guessing between two
 * candidates would be the same bug with extra steps. Returns null when the
 * rewrite cannot be placed safely; the caller must refuse rather than apply.
 */
export function resolveSelectionRewriteRange(
  doc: RewriteScanNode,
  range: { from: number; to: number },
  expectedText: string,
): { from: number; to: number } | null {
  if (!expectedText) return null

  const readRange = (from: number, to: number): string | null => {
    try {
      return doc.textBetween(from, to, ' ')
    } catch {
      return null
    }
  }

  if (range.to > range.from && readRange(range.from, range.to) === expectedText) {
    return range
  }

  if (typeof doc.descendants !== 'function') return null

  const matches: Array<{ from: number; to: number }> = []
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true
    if (matches.length > 1) return false
    let text: string
    try {
      text = node.textBetween(0, node.content.size, ' ')
    } catch {
      return false
    }
    let idx = text.indexOf(expectedText)
    while (idx !== -1 && matches.length <= 1) {
      matches.push({ from: pos + 1 + idx, to: pos + 1 + idx + expectedText.length })
      idx = text.indexOf(expectedText, idx + 1)
    }
    return false
  })

  if (matches.length !== 1) return null
  const candidate = matches[0]!
  // Block offsets map linearly only while the block holds plain text; verify
  // against the document itself before handing the range back.
  if (readRange(candidate.from, candidate.to) !== expectedText) return null
  return candidate
}

/** Chained commands the jump uses — a TipTap `Editor` satisfies this. */
interface CommentJumpChain {
  focus: () => CommentJumpChain
  setTextSelection: (position: number | AnchorRange) => CommentJumpChain
  scrollIntoView: () => CommentJumpChain
  run: () => boolean
}

/** The slice of the editor a jump touches; structural so it is testable. */
export interface CommentJumpEditor extends AnchorDocSource {
  chain: () => CommentJumpChain
}

/**
 * Land the editor on a comment.
 *
 * Row 1 — the anchor still resolves: select the range and let the focused tint
 * (painted by the decoration plugin off `focusedCommentId`) mark it. That tint
 * replaced the old whole-editor flash, which said "something happened here
 * somewhere" and nothing more.
 *
 * Row 2 — section-level, or a text anchor that has drifted beyond recovery:
 * scroll to the start of the section in a composite document, or the top of a
 * single-section editor, and paint nothing. Guessing a range would be a lie
 * about where the reviewer was looking.
 *
 * Callers must not dispatch foreign-section jumps in single mode — the card's
 * jump matrix (`resolveJumpAction` in `CommentCard`) is the gate, and it routes
 * those to the other section's editor instead.
 */
export function applyCommentJump(
  editor: CommentJumpEditor,
  detail: CommentJumpDetail,
  options: { isCompositeDocument: boolean },
): 'range' | 'section' {
  const sectionRange =
    detail.sectionId != null ? findSectionRange(editor, detail.sectionId) : null

  // A composite document delimits its sections with segmentHeading nodes. If
  // this comment's section is not one of them, `resolveAnchor`'s whole-doc
  // fallback would quote-match a neighbouring section — the same lie the
  // decoration plugin refuses to paint. A single-section editor has no
  // delimiters at all, so there the whole-doc fallback IS the resolution path.
  const canResolveRange = !options.isCompositeDocument || sectionRange != null
  const range = canResolveRange
    ? resolveAnchor(editor, {
        sectionId: detail.sectionId,
        startPos: detail.startPos,
        endPos: detail.endPos,
        quotedText: detail.quotedText,
      })
    : null

  if (range) {
    editor.chain().focus().setTextSelection(range).scrollIntoView().run()
    useCommentsStore.getState().focusComment(detail.commentId)
    return 'range'
  }

  editor.chain().focus().setTextSelection(sectionRange?.from ?? 0).scrollIntoView().run()
  return 'section'
}

// ===================
// Suggestion Plugin
// ===================

const suggestionPluginKey = new PluginKey('suggestion')

function createSuggestionPlugin() {
  let lastResult: EditSelectionResponse | null = null
  let cachedDecorations: DecorationSet = DecorationSet.empty

  return new Plugin({
    key: suggestionPluginKey,
    state: {
      init: () => DecorationSet.empty,
      apply: (tr, _old) => {
        const { selectionEditResult } = useAgentStore.getState()

        if (!selectionEditResult) {
          lastResult = null
          cachedDecorations = DecorationSet.empty
          return DecorationSet.empty
        }

        if (selectionEditResult === lastResult) {
          cachedDecorations = cachedDecorations.map(tr.mapping, tr.doc)
          return cachedDecorations
        }

        lastResult = selectionEditResult
        const { from, to } = selectionEditResult.selectionRange
        const decorations: Decoration[] = []

        tr.doc.nodesBetween(from, to, (node, pos) => {
          if (node.isTextblock) {
            const contentStart = pos + 1
            const contentEnd = pos + node.nodeSize - 1
            const decorFrom = Math.max(from, contentStart)
            const decorTo = Math.min(to, contentEnd)
            if (decorFrom < decorTo) {
              decorations.push(
                Decoration.inline(decorFrom, decorTo, { class: 'suggestion-delete' })
              )
            }
            return false
          }
          return true
        })

        if (decorations.length === 0) {
          decorations.push(Decoration.inline(from, to, { class: 'suggestion-delete' }))
        }

        const widget = document.createElement('span')
        widget.className = 'suggestion-insert'
        widget.textContent = selectionEditResult.replacementText
        decorations.push(Decoration.widget(to, widget))

        cachedDecorations = DecorationSet.create(tr.doc, decorations)
        return cachedDecorations
      },
    },
    props: {
      decorations(state) {
        return this.getState(state) ?? DecorationSet.empty
      },
    },
  })
}

// ===================
// Selection Highlight Plugin
// ===================

const selectionHighlightPluginKey = new PluginKey('selectionHighlight')

function createSelectionHighlightPlugin() {
  let lastSelection: SelectionState | null = null
  let cachedDecorations: DecorationSet = DecorationSet.empty

  return new Plugin({
    key: selectionHighlightPluginKey,
    state: {
      init: () => DecorationSet.empty,
      apply: (tr, _old) => {
        const { selection, selectionEditResult } = useAgentStore.getState()

        // Hide highlight when suggestion decorations are active
        if (selectionEditResult || !selection) {
          lastSelection = null
          cachedDecorations = DecorationSet.empty
          return DecorationSet.empty
        }

        // If range unchanged, just map through transaction
        if (
          lastSelection &&
          lastSelection.range.from === selection.range.from &&
          lastSelection.range.to === selection.range.to
        ) {
          cachedDecorations = cachedDecorations.map(tr.mapping, tr.doc)
          return cachedDecorations
        }

        lastSelection = selection
        const { from, to } = selection.range

        // Clamp to document bounds
        const docSize = tr.doc.content.size
        const safeFrom = Math.max(0, Math.min(from, docSize))
        const safeTo = Math.max(safeFrom, Math.min(to, docSize))

        if (safeFrom >= safeTo) {
          cachedDecorations = DecorationSet.empty
          return DecorationSet.empty
        }

        const decorations: Decoration[] = []

        tr.doc.nodesBetween(safeFrom, safeTo, (node, pos) => {
          if (node.isTextblock) {
            const contentStart = pos + 1
            const contentEnd = pos + node.nodeSize - 1
            const decorFrom = Math.max(safeFrom, contentStart)
            const decorTo = Math.min(safeTo, contentEnd)
            if (decorFrom < decorTo) {
              decorations.push(
                Decoration.inline(decorFrom, decorTo, { class: 'selection-highlight' })
              )
            }
            return false
          }
          return true
        })

        if (decorations.length === 0 && safeFrom < safeTo) {
          decorations.push(
            Decoration.inline(safeFrom, safeTo, { class: 'selection-highlight' })
          )
        }

        cachedDecorations = DecorationSet.create(tr.doc, decorations)
        return cachedDecorations
      },
    },
    props: {
      decorations(state) {
        return this.getState(state) ?? DecorationSet.empty
      },
    },
  })
}

// ===================
// Toolbar Button
// ===================

interface ToolbarButtonProps {
  icon: React.ReactNode
  title: string
  isActive?: boolean
  onClick: () => void
  disabled: boolean
}

function ToolbarButton({ icon, title, isActive, onClick, disabled }: ToolbarButtonProps) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('h-8 w-8', isActive && 'bg-muted')}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {icon}
    </Button>
  )
}

// ===================
// Per-hunk decision panel (Track 3)
// ===================
//
// When a proposal contains multiple independent changes, the user can accept
// some and reject others before clicking the bulk Accept. Pending hunks
// default to accepted at apply time so the user only has to act on the
// changes they want to revert.

export const HUNK_LIST_MAX = 8

interface HunkDecisionPanelProps {
  hunks: DiffHunk[]
  decisions: Record<string, 'accepted' | 'rejected' | 'pending'>
  onDecide: (hunkId: string, decision: 'accepted' | 'rejected' | 'pending') => void
  disabled: boolean
}

export function HunkDecisionPanel({ hunks, decisions, onDecide, disabled }: HunkDecisionPanelProps) {
  return (
    <div className="shrink-0 max-h-[40vh] overflow-auto border-b bg-muted/20 px-3 py-2 text-xs">
      <div className="text-muted-foreground mb-1.5">
        {hunks.length} changes — click a row to jump to it, or use ✓/✗ to accept/reject individually.
      </div>
      <ul className="space-y-1">
        {hunks.map((hunk) => {
          const decision = decisions[hunk.id] ?? 'pending'
          const previewText = previewHunkText(hunk)
          const tone =
            hunk.kind === 'modified' ? 'text-foreground'
            : hunk.kind === 'inserted' ? 'text-[hsl(var(--status-success))]'
            : 'text-destructive'
          return (
            <li key={hunk.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollToHunkInEditor(hunk.id)}
                className="flex-1 min-w-0 flex items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-muted/60"
                title={`Jump to: ${previewText}`}
              >
                <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground w-16 shrink-0">
                  {hunk.kind}
                </span>
                <span className={cn('flex-1 min-w-0 truncate', tone)}>
                  {previewText}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onDecide(hunk.id, decision === 'accepted' ? 'pending' : 'accepted')}
                disabled={disabled}
                className={cn(
                  'inline-flex items-center justify-center h-6 w-6 rounded-md border',
                  decision === 'accepted'
                    ? 'border-[hsl(var(--status-success))] bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))]'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
                aria-label={`Accept ${hunk.id}`}
                title="Accept this change"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => onDecide(hunk.id, decision === 'rejected' ? 'pending' : 'rejected')}
                disabled={disabled}
                className={cn(
                  'inline-flex items-center justify-center h-6 w-6 rounded-md border',
                  decision === 'rejected'
                    ? 'border-destructive bg-destructive/10 text-destructive'
                    : 'border-border text-muted-foreground hover:text-destructive',
                )}
                aria-label={`Reject ${hunk.id}`}
                title="Reject this change"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function previewHunkText(hunk: DiffHunk, maxLen = 80): string {
  const source =
    hunk.kind === 'inserted' ? hunk.afterHtml
    : hunk.kind === 'removed' ? hunk.beforeHtml
    : hunk.afterHtml
  const text = source.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  if (text.length <= maxLen) return text
  return text.slice(0, maxLen - 1) + '…'
}

// ===================
// Editor Toolbar
// ===================

interface EditorToolbarProps {
  editor: ReturnType<typeof useEditor>
  disabled: boolean
  onInsertImage: () => void
  onOpenVersionHistory?: (() => void) | undefined
}

function EditorToolbar({ editor, disabled, onInsertImage, onOpenVersionHistory }: EditorToolbarProps) {
  if (!editor) return null

  const handleLink = () => {
    const previousUrl = editor.getAttributes('link')['href'] as string | undefined
    const url = window.prompt('URL', previousUrl)
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 overflow-x-auto border-b">
      <ToolbarButton
        icon={<Undo2 className="h-4 w-4" />}
        title="Undo (Cmd+Z)"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
      />
      <ToolbarButton
        icon={<Redo2 className="h-4 w-4" />}
        title="Redo (Cmd+Shift+Z)"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
      />
      <Separator orientation="vertical" className="h-5 mx-1.5 bg-foreground/15" />
      <ToolbarButton icon={<Bold className="h-4 w-4" />} title="Bold (Cmd+B)" isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} disabled={disabled} />
      <ToolbarButton icon={<Italic className="h-4 w-4" />} title="Italic (Cmd+I)" isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} disabled={disabled} />
      <ToolbarButton icon={<Underline className="h-4 w-4" />} title="Underline (Cmd+U)" isActive={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} disabled={disabled} />
      <ToolbarButton icon={<Strikethrough className="h-4 w-4" />} title="Strikethrough" isActive={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} disabled={disabled} />
      <Separator orientation="vertical" className="h-5 mx-1.5 bg-foreground/15" />
      <ToolbarButton icon={<Heading1 className="h-4 w-4" />} title="Heading 1" isActive={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} disabled={disabled} />
      <ToolbarButton icon={<Heading2 className="h-4 w-4" />} title="Heading 2" isActive={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} disabled={disabled} />
      <ToolbarButton icon={<Heading3 className="h-4 w-4" />} title="Heading 3" isActive={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} disabled={disabled} />
      <Separator orientation="vertical" className="h-5 mx-1.5 bg-foreground/15" />
      <ToolbarButton icon={<List className="h-4 w-4" />} title="Bullet List" isActive={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} disabled={disabled} />
      <ToolbarButton icon={<ListOrdered className="h-4 w-4" />} title="Numbered List" isActive={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} disabled={disabled} />
      <Separator orientation="vertical" className="h-5 mx-1.5 bg-foreground/15" />
      <ToolbarButton icon={<Quote className="h-4 w-4" />} title="Blockquote" isActive={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} disabled={disabled} />
      <ToolbarButton icon={<Code className="h-4 w-4" />} title="Code Block" isActive={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} disabled={disabled} />
      <Separator orientation="vertical" className="h-5 mx-1.5 bg-foreground/15" />
      <ToolbarButton icon={<Link className="h-4 w-4" />} title="Insert Link" isActive={editor.isActive('link')} onClick={handleLink} disabled={disabled} />
      <ToolbarButton icon={<Table className="h-4 w-4" />} title="Insert Table" isActive={editor.isActive('table')} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} disabled={disabled} />
      <ToolbarButton icon={<ImageIcon className="h-4 w-4" />} title="Insert Image" onClick={onInsertImage} disabled={disabled || !editor.isEditable} />
      {onOpenVersionHistory && (
        <>
          <Separator orientation="vertical" className="h-5 mx-1.5 bg-foreground/15" />
          <ToolbarButton icon={<History className="h-4 w-4" />} title="Version history" onClick={onOpenVersionHistory} disabled={false} />
        </>
      )}
    </div>
  )
}

// ===================
// Save Status Indicator
// ===================

export function SaveStatusIndicator({
  status,
  retriesExhausted = false,
  onRetry,
}: {
  status: SaveStatus
  retriesExhausted?: boolean
  onRetry?: () => void
}) {
  return (
    <div className="flex items-center gap-1.5 text-meta">
      {status === 'saving' && (<><Loader2 className="h-3 w-3 animate-spin" /><span>Saving...</span></>)}
      {status === 'saved' && (<><Check className="h-3 w-3 text-[hsl(var(--status-success))]" /><span>Saved</span></>)}
      {status === 'unsaved' && <span className="text-[hsl(var(--status-warning))]">Unsaved changes</span>}
      {status === 'error' && (
        <>
          <span className="text-destructive">Save failed</span>
          {retriesExhausted
            ? onRetry && (
                <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={onRetry}>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )
            : <span>— retrying…</span>}
        </>
      )}
      {status === 'blocked' && (<><Lock className="h-3 w-3" /><span>Editing blocked</span></>)}
    </div>
  )
}

// ===================
// Blocked-state Backup Banner (WF4)
// ===================

interface BlockedEditorBannerProps {
  message: string
  backupDone: boolean
  onBackup: () => void
}

export function BlockedEditorBanner({ message, backupDone, onBackup }: BlockedEditorBannerProps) {
  return (
    <div className="mx-4 mt-2 flex items-center justify-between gap-3 rounded-lg bg-[hsl(var(--status-warning)/0.12)] px-3 py-2 text-sm text-foreground">
      <span>
        {backupDone
          ? 'Backed up — the owner can restore it from version history.'
          : message}
      </span>
      {!backupDone && (
        <Button variant="secondary" size="sm" className="shrink-0" onClick={onBackup}>
          Save backup
        </Button>
      )}
    </div>
  )
}

// ===================
// Failed-save Restore Banner (WF4)
// ===================

interface DraftMirrorRestoreBannerProps {
  onRestore: () => void
  onDiscard: () => void
}

export function DraftMirrorRestoreBanner({ onRestore, onDiscard }: DraftMirrorRestoreBannerProps) {
  return (
    <div className="mx-4 mt-2 flex items-center justify-between gap-3 rounded-lg bg-[hsl(var(--status-info)/0.12)] px-3 py-2 text-sm text-foreground">
      <span>You have unsaved changes from a previous session.</span>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="secondary" size="sm" onClick={onRestore}>Restore</Button>
        <Button variant="ghost" size="sm" onClick={onDiscard}>Discard</Button>
      </div>
    </div>
  )
}

// ===================
// Stale Segment Banner
// ===================

interface StaleSegmentBannerProps {
  staleSegments: DraftSegment[]
  onRegenerate: (sectionIds: string[]) => void
  /** A turn is already running — regenerating now would orphan its stream. */
  isTurnBusy: boolean
}

/**
 * With two or more stale sections this banner used to render text only: it told
 * the writer the drafts no longer match the plan and then offered nothing to
 * click. The action now covers every stale section in one turn — see
 * `regenerateStaleSegments` for why it is one turn and not N.
 */
export function StaleSegmentBanner({ staleSegments, onRegenerate, isTurnBusy }: StaleSegmentBannerProps) {
  if (staleSegments.length === 0) return null
  const firstStale = staleSegments[0]
  const isSingle = staleSegments.length === 1 && firstStale
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-[hsl(var(--status-warning)/0.08)] border-b border-[hsl(var(--status-warning)/0.35)] text-xs text-foreground">
      <AlertTriangle className="h-3 w-3 flex-shrink-0 text-[hsl(var(--status-warning))]" />
      <span className="min-w-0">
        {isSingle
          ? `"${firstStale.heading}" needs regeneration (plan updated)`
          : `${staleSegments.length} sections need regeneration (plan updated)`}
      </span>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {/* Say why rather than leave a dead button: an ungated click just
            returns "Please wait for the current background task to finish". */}
        {isTurnBusy && <span className="text-muted-foreground">Working — regenerate when this finishes</span>}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs"
          disabled={isTurnBusy}
          onClick={() => onRegenerate(staleSegments.map((segment) => segment.sectionId))}
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          {isSingle ? 'Regenerate' : 'Regenerate all'}
        </Button>
      </div>
    </div>
  )
}

// ===================
// Main Component
// ===================

export function DraftEditor({ artifactId, bidId, sectionKey, section, documentSectionNumber: _documentSectionNumber, canWrite: canWriteProp = true, lockedByUser, lockedReason, collaborationStatus, onCollaborationStatusChange, isReviewer, isOwner: _isOwner, writerName, bidMembers, currentUserId, latestReviewRound, plannedReviewerId, onRequestReview, onSubmitReview, placeholderText, onBack, headerTitle, belowToolbarSlot, headerStatusSlot, footerStats }: DraftEditorProps) {
  // A read-only user — an org viewer, or a member who is not on the bid —
  // never writes, whatever the page decided (the backend refuses them every
  // bid write); the editor opens read-only with the reason in the banner.
  const bidAccess = useBidReadOnly(bidId)
  const canWrite = canWriteProp && !bidAccess.isReadOnly
  // Export is a POST only an org viewer is refused: a member who is not on the
  // bid may still read it, and so export it.
  const isViewOnly = useIsViewOnly()
  const {
    segments,
    planSectionOrder,
    initSession,
    sessionInitContext,
    setSelection,
    setApplySelectionRewrite,
    setThreadTransitionSaveFlusher,
    regenerateStaleSegments,
  } = useAgentStore()

  const selection = useAgentStore((s) => s.selection)
  const selectionEditResult = useAgentStore((s) => s.selectionEditResult)
  const isEditingSelection = useAgentStore((s) => s.isEditingSelection)
  const clearSelectionEdit = useAgentStore((s) => s.clearSelectionEdit)
  const applySelectionRewrite = useAgentStore((s) => s.applySelectionRewrite)
  const draftPatchPreview = useAgentStore((s) => s.draftPatchPreview)
  const isResolvingDraftPatchPreview = useAgentStore((s) => s.isResolvingDraftPatchPreview)
  const acceptDraftPatchPreview = useAgentStore((s) => s.acceptDraftPatchPreview)
  const rejectDraftPatchPreview = useAgentStore((s) => s.rejectDraftPatchPreview)

  // A staged suggestion (R1) is diffed over the section's CURRENT text — the
  // editor never holds the AI text. Legacy proposals diff over `before`. An
  // outdated suggestion renders no diff: the editor shows the current text.
  const isPreviewOutdated = Boolean(draftPatchPreview?.outdated)
  const segmentDiffData = useMemo(
    () => draftPatchPreview && !draftPatchPreview.outdated
      ? buildSegmentDiff(previewDiffBase(draftPatchPreview, segments), draftPatchPreview.after)
      : null,
    [draftPatchPreview, segments]
  )

  const diffHtml = segmentDiffData?.html ?? null

  const diffStats = useMemo(
    () => diffHtml ? countDiffWords(diffHtml) : null,
    [diffHtml]
  )

  const setHunkDecision = useAgentStore((s) => s.setHunkDecision)

  const patchSectionName = draftPatchPreview
    ? segments.find((s) => s.sectionId === draftPatchPreview.sectionId)?.heading
    : undefined

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved')
  const [conflict, setConflict] = useState<ConflictInfo | null>(null)
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)
  // Which segment the open history dialog addresses. Resolved at open time
  // because in a composite document it depends on where the caret sits.
  const [versionHistoryTarget, setVersionHistoryTarget] = useState<
    { sectionId: string; heading: string | null } | null
  >(null)
  const [blockedReason, setBlockedReason] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  // Evaluator view — spec 19. A CLOSE PREVIEW of the export, not a reproduction
  // of it: the container class hides annotation chips, gap counts and citation
  // markers (src/index.css), and the editor goes read-only for as long as it is
  // on. The space before a hidden token is hidden too, so spacing matches the
  // export for the common cases; one edge remains ("x [1][TODO: y]." reads
  // "x ."). See the `.evaluator-view` block in src/index.css.
  const [isEvaluatorView, setIsEvaluatorView] = useState(false)
  const [wordCount, setWordCount] = useState(0)
  const [citationTooltip, setCitationTooltip] = useState<CitationTooltipState | null>(null)
  // WF4 autosave resilience
  const [retriesExhausted, setRetriesExhausted] = useState(false)
  const [backupDone, setBackupDone] = useState(false)
  const [mirrorBanner, setMirrorBanner] = useState<DraftMirror | null>(null)
  const saveRetryCountRef = useRef(0)
  // One autosave request in flight at a time (see saveQueue.ts). Per editor
  // instance: the section pages key the editor by section, so a section switch
  // gets a fresh queue.
  const [saveQueue] = useState(createSaveQueue)
  // Texts this editor sent and never saw confirmed (a save that timed out may
  // still have landed), so the retry's 409 against one of them is adopted
  // rather than shown as a conflict — see unconfirmedSaves.ts.
  const [unconfirmedSaves] = useState(createUnconfirmedSaveLog)
  // Bumped by every failed save, so the retry timer is re-planned even when
  // the status was already 'error' and so did not visibly change.
  const [failedSaveCount, setFailedSaveCount] = useState(0)

  // The one autosave timer (debounce or retry). Whoever clears it also nulls
  // it: scheduleAutosave reads "a timer is pending" from this ref.
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Autosave arming — see scheduleAutosave. Refs, because the TipTap update
  // handler is created once and must still reach the current function.
  const isMountedRef = useRef(false)
  const autosaveSuspendedRef = useRef(false)
  const scheduleAutosaveRef = useRef<() => void>(() => {})
  const isUpdatingFromStoreRef = useRef(false)
  const isDirtyRef = useRef(false)
  const previousContentRef = useRef<string>('')
  const pendingContentRef = useRef<string | null>(null)
  const previousSessionIdRef = useRef<string | null>(null)
  const previousSectionIdentityRef = useRef<string | null>(null)
  const previousSegmentSnapshotRef = useRef<Map<string, string>>(new Map())
  const isOwnSaveRef = useRef(false)
  // Stable refs the editorProps closures use so they read fresh values
  // even though editorProps are captured once at editor-creation time.
  const editorInstanceRef = useRef<ReturnType<typeof useEditor> | null>(null)
  const artifactIdRef = useRef(artifactId)
  artifactIdRef.current = artifactId
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const isMouseDownRef = useRef(false)
  const pendingSelectionRef = useRef<{ text: string; range: { from: number; to: number }; sectionId: string } | null>(null)

  const orderedSegments = useMemo(() => {
    if (planSectionOrder.length === 0) return segments

    const byId = new Map(segments.map((segment) => [segment.sectionId, segment]))
    const ordered = planSectionOrder
      .map((id) => byId.get(id))
      .filter((segment): segment is DraftSegment => Boolean(segment))

    if (ordered.length === segments.length) return ordered

    const orderedIds = new Set(ordered.map((segment) => segment.sectionId))
    const extras = segments.filter((segment) => !orderedIds.has(segment.sectionId))
    return [...ordered, ...extras]
  }, [segments, planSectionOrder])

  const isCompositeDocument = sectionKey === DOCUMENT_SECTION_KEY && orderedSegments.length > 1
  // Memoised because the store→editor sync effect depends on it. A section with
  // no plan (written by hand) hydrates with `planSectionOrder: []`; rebuilding
  // the order on every render then re-ran that effect on every keystroke and
  // loaded the saved text back over the writer's typing.
  const effectiveSectionOrder = useMemo(
    () => (planSectionOrder.length > 0
      ? planSectionOrder
      : orderedSegments.map((segment) => segment.sectionId)),
    [planSectionOrder, orderedSegments]
  )

  const currentSegment = orderedSegments.find((s) => s.sectionId === sectionKey) ?? orderedSegments[0] ?? null

  // Whether the proposal's target section is actually on screen. The diff is only
  // injected into the editor when it is (see the content effect below), so the
  // "review them in the document below" copy is a lie on a mismatch — which is
  // routine, because a proposal can address a section this editor is not showing.
  const isPatchTargetVisible = Boolean(draftPatchPreview && (
    isCompositeDocument
      ? orderedSegments.some((s) => s.sectionId === draftPatchPreview.sectionId)
      : currentSegment?.sectionId === draftPatchPreview.sectionId
  ))

  // The key a segment is persisted under is not always the outline section key:
  // a unit-scoped session stores its single segment as 'overview'. Saves already
  // address the segment through `currentSegment`, so every other segment-scoped
  // call (version history, restore, backup) must resolve the same way or it
  // addresses a segment that was never written.
  //
  // Deliberately stricter than `currentSegment`, which falls back to
  // orderedSegments[0] purely so the editor has something to render. These calls
  // MUTATE (restore rewrites content, backup files a version row), so the
  // single-segment case is the only ambiguity worth resolving: a session holding
  // one segment under a different key IS this section. With several segments and
  // no exact match there is no safe guess, so keep `sectionKey` and let the
  // backend 404 — the pre-fix behaviour, which is correct for a key nothing was
  // written under. `sectionKey` is also the right answer when no segment exists
  // yet: it is the key the first save creates.
  //
  // The composite document is the one multi-segment case that IS resolvable, and
  // it is handled separately by `resolveSegmentScopedTarget` below — see the note
  // there. This value stays the per-section answer.
  const exactSegment = orderedSegments.find((s) => s.sectionId === sectionKey)
  const soleSegment = orderedSegments.length === 1 ? orderedSegments[0] : undefined
  const persistedSectionId = (exactSegment ?? soleSegment)?.sectionId ?? sectionKey

  // Build citation refs map from segments (sectionId → citationRefs). While a
  // staged suggestion is on screen, its section's markers are the suggestion's
  // and resolve against its own refs (R1 W2).
  const citationRefsBySectionId = useMemo(() => {
    const map = new Map<string, DraftSegment['citationRefs']>()
    for (const seg of orderedSegments) {
      if (seg.citationRefs?.length > 0) {
        map.set(seg.sectionId, seg.citationRefs)
      }
    }
    return withPreviewCitationRefs(map, draftPatchPreview, 'citation')
  }, [orderedSegments, draftPatchPreview])

  // Build library citation refs map from segments (sectionId → libraryCitationRefs)
  const libraryCitationRefsBySectionId = useMemo(() => {
    const map = new Map<string, DraftSegment['libraryCitationRefs']>()
    for (const seg of orderedSegments) {
      if (seg.libraryCitationRefs?.length > 0) {
        map.set(seg.sectionId, seg.libraryCitationRefs)
      }
    }
    return withPreviewCitationRefs(map, draftPatchPreview, 'library')
  }, [orderedSegments, draftPatchPreview])

  // Fallback for single-section editors (no segmentHeading nodes)
  const isPreviewOnCurrentSegment = Boolean(
    draftPatchPreview && currentSegment && draftPatchPreview.sectionId === currentSegment.sectionId
  )
  const fallbackCitationRefs = useMemo(
    () => (isPreviewOnCurrentSegment ? previewOwnRefs(draftPatchPreview, 'citation') : null)
      ?? currentSegment?.citationRefs
      ?? [],
    [currentSegment, draftPatchPreview, isPreviewOnCurrentSegment]
  )
  const fallbackLibraryCitationRefs = useMemo(
    () => (isPreviewOnCurrentSegment ? previewOwnRefs(draftPatchPreview, 'library') : null)
      ?? currentSegment?.libraryCitationRefs
      ?? [],
    [currentSegment, draftPatchPreview, isPreviewOnCurrentSegment]
  )
  // A never-drafted section has no segment to derive the session from — fall
  // back to the store's active session so the first save can create the segment.
  const storeSessionId = useAgentStore((s) => s.sessionId)
  const currentSessionId = currentSegment?.sessionId ?? storeSessionId ?? null

  const { editors: activeEditors } = useEditingPresence(
    currentSessionId ?? null,
    sectionKey,
    canWrite && !isCompositeDocument
  )

  const staleSegments = isCompositeDocument
    ? orderedSegments.filter((segment) => segment.status === 'stale')
    : (currentSegment && currentSegment.status === 'stale' ? [currentSegment] : [])

  // The stale banner is a second door onto `sendTurn` from outside the composer,
  // so it carries the composer's gate itself (same three flags the plan card
  // uses). The ref is the synchronous half: `sendTurn` awaits a draft flush and
  // a token fetch before it raises `isStreaming`, and a double-click lands
  // inside that window.
  const isAgentStreaming = useAgentStore((s) => s.isStreaming)
  const isAgentPolling = useAgentStore((s) => s.isPolling)
  const isAgentCancellable = useAgentStore((s) => s.isCancellable)
  const isRegenerateBusy = isAgentStreaming || isAgentPolling || isAgentCancellable
  const regenerateStaleInflightRef = useRef(false)
  const handleRegenerateStale = useCallback(
    (sectionIds: string[]) => {
      if (regenerateStaleInflightRef.current) return
      regenerateStaleInflightRef.current = true
      void regenerateStaleSegments(sectionIds).finally(() => {
        regenerateStaleInflightRef.current = false
      })
    },
    [regenerateStaleSegments]
  )

  // Reset local save tracking when editing target changes, even if sessionId is reused.
  useEffect(() => {
    const sectionIdentity = `${artifactId}:${sectionKey}`
    if (previousSectionIdentityRef.current === sectionIdentity) return

    previousSectionIdentityRef.current = sectionIdentity
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }

    isDirtyRef.current = false
    pendingContentRef.current = null
    previousContentRef.current = ''
    previousSessionIdRef.current = null
    previousSegmentSnapshotRef.current = new Map()
    setConflict(null)
    setBlockedReason(null)
    setSaveStatus('saved')
  }, [artifactId, sectionKey])

  // Image insert: upload file -> S3, then insert <img> + caption placeholder
  // paragraph in a single transaction. Caption convention: §7.5.
  const insertImageFromFile = useCallback(async (file: File) => {
    const editor = editorInstanceRef.current
    if (!editor) return
    if (!editor.isEditable) return
    const currentArtifactId = artifactIdRef.current
    if (!currentArtifactId) return

    try {
      const uploaded = await uploadEditorImage(file, currentArtifactId)
      // Insert as TipTap nodes via insertContent — this avoids depending on
      // the Image extension's typed `setImage` chain command (which the
      // resize-image build doesn't surface in its type signature).
      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'imageResize',
            attrs: {
              src: uploaded.src,
              alt: '',
              'data-image-id': uploaded.imageId,
              'data-width': String(uploaded.width),
              'data-height': String(uploaded.height),
              width: uploaded.width,
            },
          },
          {
            type: 'paragraph',
            attrs: { class: 'image-caption' },
          },
        ])
        .run()
    } catch (err) {
      const message = err instanceof EditorImageUploadError
        ? err.message
        : 'Failed to upload image'
      toast.error(message)
    }
  }, [])

  const triggerImagePicker = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const onImageFilePicked = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      void insertImageFromFile(file)
    }
    // Reset so picking the same file twice still fires onChange.
    event.target.value = ''
  }, [insertImageFromFile])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: {
          openOnClick: false,
          HTMLAttributes: { class: 'text-[hsl(var(--link))] underline cursor-pointer' },
        },
      }),
      TableExtension.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      ImageResize.configure({
        inline: false,
      }),
      // Allow a `class` attribute on paragraph nodes so the caption convention
      // (`<p class="image-caption">`) round-trips through TipTap's HTML
      // serializer / parser. Specs/editor-images-spec.md §7.5.
      Extension.create({
        name: 'paragraphClassAttribute',
        addGlobalAttributes() {
          return [
            {
              types: ['paragraph'],
              attributes: {
                class: {
                  default: null,
                  parseHTML: (element) => element.getAttribute('class'),
                  renderHTML: (attributes) => {
                    const cls = attributes['class']
                    if (!cls) return {}
                    return { class: cls as string }
                  },
                },
              },
            },
          ]
        },
      }),
      // Round-trip data-hunk-id through TipTap so the diff-preview fragments
      // keep their hunk addresses in the rendered DOM (scroll-to targets).
      // Preview HTML is never persisted — accept/reject rebuilds content from
      // buildSegmentDiff(before, after) in the store — so this cannot leak
      // into saved documents.
      Extension.create({
        name: 'hunkIdAttribute',
        addGlobalAttributes() {
          return [
            {
              types: ['paragraph', 'heading', 'bulletList', 'orderedList', 'blockquote', 'table'],
              attributes: {
                'data-hunk-id': {
                  default: null,
                  parseHTML: (element: HTMLElement) => element.getAttribute('data-hunk-id'),
                  renderHTML: (attributes: Record<string, unknown>) => {
                    const id = attributes['data-hunk-id']
                    if (!id) return {}
                    return { 'data-hunk-id': String(id) }
                  },
                },
              },
            },
          ]
        },
      }),
      // Preserve data-image-id, data-width, data-height on imageResize nodes
      // through TipTap serialization. The base `tiptap-extension-resize-image`
      // only declares `containerStyle` and `wrapperStyle`, so any custom
      // attributes we set on insert get dropped on `getHTML()`. The DOCX
      // export parsers (docx-builder.ts, docx-template-builder.ts) require
      // data-image-id to identify the S3 key and data-width/data-height for
      // sizing — without these the image is silently dropped from exports.
      Extension.create({
        name: 'imageResizeDataAttributes',
        addGlobalAttributes() {
          const passThroughDataAttr = (name: string) => ({
            default: null as string | null,
            parseHTML: (element: HTMLElement) => element.getAttribute(name),
            renderHTML: (attributes: Record<string, unknown>) => {
              const value = attributes[name]
              if (value === null || value === undefined || value === '') return {}
              return { [name]: String(value) }
            },
          })
          return [
            {
              types: ['imageResize'],
              attributes: {
                'data-image-id': passThroughDataAttr('data-image-id'),
                'data-width': passThroughDataAttr('data-width'),
                'data-height': passThroughDataAttr('data-height'),
              },
            },
          ]
        },
      }),
      SegmentHeading,
      ...diffMarks,
      Placeholder.configure({
        placeholder: placeholderText
          ?? (sectionKey === DOCUMENT_SECTION_KEY
            ? 'Start writing your document...\n\nTip: Use the AI panel on the right to draft, revise, or refine this document in one chat.'
            : `Start writing your ${section.title} section...\n\nTip: Use the AI panel on the right to draft, revise, or refine this section in one chat.`),
      }),
      Extension.create({
        name: 'segmentStatus',
        addProseMirrorPlugins() {
          return [segmentStatusPlugin]
        },
      }),
      Extension.create({
        name: 'suggestion',
        addProseMirrorPlugins() {
          return [createSuggestionPlugin()]
        },
      }),
      Extension.create({
        name: 'selectionHighlight',
        addProseMirrorPlugins() {
          return [createSelectionHighlightPlugin()]
        },
      }),
      Extension.create({
        name: 'citationDecoration',
        addProseMirrorPlugins() {
          return [createCitationDecorationPlugin()]
        },
      }),
      Extension.create({
        name: 'annotationDecoration',
        addProseMirrorPlugins() {
          return [createAnnotationDecorationPlugin()]
        },
      }),
      // A composite document delimits its sections with segmentHeading nodes, so
      // any comment whose section is present may paint. Every other editor —
      // section, questionnaire, single-segment document — is one section with no
      // delimiters, where anchor resolution falls back to the whole doc: without
      // the section filter another section's quote would match here.
      Extension.create({
        name: 'commentDecoration',
        addProseMirrorPlugins() {
          return [
            createCommentDecorationPlugin({
              mode: isCompositeDocument ? 'composite' : 'single',
              currentSectionKey: isCompositeDocument ? null : sectionKey,
            }),
          ]
        },
      }),
    ],
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none p-4 outline-none min-h-full',
      },
      handleClick(_view, _pos, event) {
        const target = event.target as HTMLElement
        const badge = target.closest<HTMLElement>('.citation-badge-inline')
        if (!badge) {
          // Click-to-thread: a commented range opens its card in the panel.
          // `focusComment` also tints the range — the decoration feed repaints
          // on every focus change.
          const commentId = target.closest<HTMLElement>('[data-comment-id]')?.dataset['commentId']
          if (commentId) {
            useAgentStore.getState().openPanel()
            useUnifiedAiPanelStore.getState().setTab('comment')
            useCommentsStore.getState().focusComment(commentId)
            return true
          }
          return false
        }

        const citationIndex = parseInt(badge.dataset['citationIndex'] ?? '', 10)
        const sectionId = badge.dataset['sectionId'] ?? ''
        const citationType = badge.dataset['citationType'] === 'library' ? 'library' as const : 'tender' as const
        if (!citationIndex || citationIndex < 1) return false

        const rect = badge.getBoundingClientRect()
        setCitationTooltip({ citationIndex, sectionId, citationType, anchorRect: rect })
        return true
      },
      handlePaste(view, event) {
        if (!view.editable) return false
        const items = event.clipboardData?.items
        if (!items) return false
        const imageItem = Array.from(items).find((i) => i.type.startsWith('image/'))
        if (!imageItem) return false
        const file = imageItem.getAsFile()
        if (!file) return false
        event.preventDefault()
        void insertImageFromFile(file)
        return true
      },
      handleDrop(view, event) {
        if (!view.editable) return false
        const dt = event.dataTransfer
        const file = dt?.files?.[0]
        if (!file || !file.type.startsWith('image/')) return false
        event.preventDefault()
        void insertImageFromFile(file)
        return true
      },
    },
    onUpdate: ({ editor: e, transaction }) => {
      if (isUpdatingFromStoreRef.current) return
      if (transaction.getMeta('suggestion-update')) return
      if (transaction.getMeta('selection-highlight-update')) return
      if (transaction.getMeta('comment-decoration-update')) return

      // While a draft-patch preview is live the editor's document is the DIFF
      // (insertion/deletion marks), not the draft, and the editor is read-only —
      // nothing here is user-authored. Marking it dirty let autosave PUT the diff
      // markup as the section's content (E2E F1). The preview's accept/reject
      // path owns the next write.
      if (useAgentStore.getState().draftPatchPreview) return

      if (isCompositeDocument) {
        if (!transaction.docChanged) return
        const content = getNormalizedEditorHtml(e)
        isDirtyRef.current = true
        setSaveStatus('unsaved')
        scheduleAutosaveRef.current()
        setWordCount(countWords(content))
        return
      }

      const content = getNormalizedEditorHtml(e)
      if (content === previousContentRef.current) return

      isDirtyRef.current = true
      setSaveStatus('unsaved')
      pendingContentRef.current = content
      previousContentRef.current = content
      scheduleAutosaveRef.current()
      setWordCount(countWords(content))
    },
    onSelectionUpdate: ({ editor: e }) => {
      const { isEditingSelection: editing, selectionEditResult: result } =
        useAgentStore.getState()

      // Don't update selection while an edit is in-flight or a suggestion is shown
      if (editing || result) return

      const { from, to } = e.state.selection
      if (from !== to) {
        if (isCompositeDocument && isSelectionCrossSegment(e)) {
          pendingSelectionRef.current = null
          if (!isMouseDownRef.current) setSelection(null)
          return
        }
        const selectedText = e.state.doc.textBetween(from, to, ' ')
        if (selectedText.trim()) {
          const selectionSectionId = isCompositeDocument
            ? getSelectionSegmentId(e)
            : sectionKey

          if (!selectionSectionId) {
            pendingSelectionRef.current = null
            if (!isMouseDownRef.current) setSelection(null)
            return
          }
          const sel = {
            text: selectedText,
            range: { from, to },
            sectionId: selectionSectionId,
          }
          // Defer selection update while user is still dragging
          if (isMouseDownRef.current) {
            pendingSelectionRef.current = sel
          } else {
            setSelection(sel)
          }
        }
      } else {
        pendingSelectionRef.current = null
        if (!isMouseDownRef.current) setSelection(null)
      }
    },
  }, [isCompositeDocument, section.title, sectionKey, setSelection, placeholderText])

  // Keep the editor-instance ref in sync so editorProps closures
  // (handlePaste/handleDrop) can read the latest editor without stale captures.
  editorInstanceRef.current = editor

  // Paint open comment anchors. The store is filled page-level by
  // `useCommentsPolling`, so highlights are live whether or not the Comments
  // tab is open — and the artifact key is the route's, the same one the poll and
  // the comment API calls use.
  useCommentDecorationFeed(editor, artifactId)

  // Commit pending selection on mouseup (global listener to catch releases outside editor)
  useEffect(() => {
    const handleMouseUp = () => {
      if (!isMouseDownRef.current) return
      isMouseDownRef.current = false
      const pending = pendingSelectionRef.current
      pendingSelectionRef.current = null
      if (pending) {
        setSelection(pending)
      } else {
        // User clicked without dragging a range — clear any stale selection
        const { selection } = useAgentStore.getState()
        if (selection) setSelection(null)
      }
    }
    document.addEventListener('mouseup', handleMouseUp)
    return () => document.removeEventListener('mouseup', handleMouseUp)
  }, [setSelection])

  // Listen for comment:jump events emitted by the Comments tab.
  useEffect(() => {
    if (!editor) return
    function handleCommentJump(e: Event) {
      const detail = (e as CustomEvent<CommentJumpDetail>).detail
      if (!detail || !editor) return
      applyCommentJump(editor, detail, { isCompositeDocument })
    }
    window.addEventListener('comment:jump', handleCommentJump)
    return () => window.removeEventListener('comment:jump', handleCommentJump)
  }, [editor, isCompositeDocument])

  // Clear editor on session change (must run BEFORE segment load effect)
  useEffect(() => {
    if (!editor) return
    const sessionId = currentSessionId ?? null
    if (previousSessionIdRef.current !== sessionId) {
      useAgentStore.getState().clearSelectionEdit()
      previousSessionIdRef.current = sessionId
      // Only clear if we have no segments to show — avoids wiping content
      // that the segment-load effect is about to set in the same render cycle
      if (segments.length === 0) {
        isUpdatingFromStoreRef.current = true
        editor
          .chain()
          .command(({ tr }) => {
            tr.setMeta('addToHistory', false)
            return true
          })
          .clearContent(false)
          .run()
        setWordCount(0)
        setSaveStatus('saved')
        isUpdatingFromStoreRef.current = false
      }
      pendingContentRef.current = null
      previousContentRef.current = ''
      previousSegmentSnapshotRef.current = new Map()
    }
  }, [editor, currentSessionId, segments.length])

  // Load current section content into editor when the backing segment changes.
  // Skip when the store update came from our own autosave (isOwnSaveRef) to avoid
  // resetting the cursor position while the user is typing.
  useEffect(() => {
    if (!editor) return

    if (isOwnSaveRef.current) {
      isOwnSaveRef.current = false
      // Still update metadata (word count, save status) but don't touch editor content
      if (isCompositeDocument) {
        setWordCount(orderedSegments.reduce((total, segment) => total + segment.wordCount, 0))
      } else {
        const rawContent = currentSegment?.content ?? ''
        setWordCount(currentSegment?.wordCount ?? countWords(rawContent))
      }
      // A follow-up save may already be out (it starts the moment this one
      // answers, and clears the dirty flag as it starts). It sets the footer
      // itself when it lands; "Saved" now would be claimed too early and would
      // drop the close-tab guard while text is still unsent.
      if (!isDirtyRef.current && !saveQueue.isSaving()) setSaveStatus('saved')
      return
    }

    // A never-drafted section has no backing segment yet, so the store has
    // nothing newer to load — don't clobber unsaved typing with ''. The first
    // autosave creates the segment and flows back through isOwnSaveRef.
    if (!isCompositeDocument && !currentSegment && isDirtyRef.current) {
      return
    }

    isUpdatingFromStoreRef.current = true

    // Programmatic loads (initial fetch, patch acceptance) must not pollute the
    // user's undo stack — otherwise undo after accept pops the whole replace
    // transaction and leaves the editor empty.
    const loadWithoutHistory = (content: Parameters<typeof editor.commands.setContent>[0] | null) => {
      editor
        .chain()
        .command(({ tr }) => {
          tr.setMeta('addToHistory', false)
          return true
        })
        .setContent(content ?? '', { emitUpdate: false })
        .run()
    }

    if (isCompositeDocument) {
      // While a draft-patch preview is active, inline the diff for the affected
      // segment so the user reviews changes in the editor's rendered context
      // (tables, lists, citations) instead of in a separate markdown panel.
      const segmentsForRender = (draftPatchPreview && diffHtml)
        ? orderedSegments.map((segment) =>
            segment.sectionId === draftPatchPreview.sectionId
              ? { ...segment, content: diffHtml, status: 'fresh' as const }
              : segment
          )
        : orderedSegments
      const assembled = assembleEditorDocument(segmentsForRender, effectiveSectionOrder)
      loadWithoutHistory(assembled)
      previousSegmentSnapshotRef.current = extractSegmentsFromEditor(editor)
      pendingContentRef.current = null
      previousContentRef.current = ''
      setWordCount(orderedSegments.reduce((total, segment) => total + segment.wordCount, 0))
    } else {
      const rawContent = (draftPatchPreview && diffHtml && currentSegment?.sectionId === draftPatchPreview.sectionId)
        ? diffHtml
        : (currentSegment?.content ?? '')
      const normalized = rawContent ? normalizeRichTextForEditor(rawContent) : ''

      loadWithoutHistory(normalized)
      // Keep comparison format aligned with getNormalizedEditorHtml output.
      previousContentRef.current = normalized
      pendingContentRef.current = null
      setWordCount(currentSegment?.wordCount ?? countWords(rawContent))
    }

    if (!isDirtyRef.current && !saveQueue.isSaving()) {
      setSaveStatus('saved')
    }
    isUpdatingFromStoreRef.current = false
  }, [
    editor,
    isCompositeDocument,
    orderedSegments,
    effectiveSectionOrder,
    currentSegment?.id,
    currentSegment?.content,
    currentSegment?.wordCount,
    currentSegment?.sectionId,
    draftPatchPreview,
    diffHtml,
    saveQueue,
  ])

  // Auto-scroll to the first changed block whenever a new patch preview
  // becomes live. Without this the user sees the Accept/Reject toolbar but
  // has to hunt for the actual diff if it's outside the viewport — which
  // happens routinely on long sections.
  const previousProposalIdRef = useRef<string | null>(null)
  useEffect(() => {
    const proposalId = draftPatchPreview?.proposalId ?? null
    if (proposalId && proposalId !== previousProposalIdRef.current) {
      // Defer one frame so TipTap has flushed the diff content into the DOM.
      const id = requestAnimationFrame(() => {
        const root = document.querySelector('.ProseMirror') ?? document.querySelector('[contenteditable="true"]')
        const target = root?.querySelector('[data-hunk-id]') ?? root?.querySelector('ins, del')
        target?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      })
      previousProposalIdRef.current = proposalId
      return () => cancelAnimationFrame(id)
    }
    if (!proposalId) {
      previousProposalIdRef.current = null
    }
    return undefined
  }, [draftPatchPreview?.proposalId])

  const applyRewriteToEditor = useCallback((from: number, to: number, replacement: string) => {
    if (!editor) {
      throw new Error('Editor is not ready')
    }

    const normalizedReplacement = normalizeRichTextForEditor(replacement)
    const docSize = editor.state.doc.content.size
    const safeFrom = Math.max(1, Math.min(from, docSize + 1))
    const safeTo = Math.max(safeFrom, Math.min(to, docSize + 1))

    const applied = editor.chain()
      .focus()
      .setTextSelection({ from: safeFrom, to: safeTo })
      .deleteSelection()
      .insertContent(normalizedReplacement)
      .run()

    if (!applied) {
      throw new Error('Could not apply selection rewrite')
    }
    const content = getNormalizedEditorHtml(editor)
    if (!isCompositeDocument) {
      pendingContentRef.current = content
      previousContentRef.current = content
    }
    isDirtyRef.current = true
    setSaveStatus('unsaved')
    scheduleAutosaveRef.current()
    setWordCount(countWords(content))
  }, [editor, isCompositeDocument])

  // Register editor-based rewrite callback
  useEffect(() => {
    if (!editor) return
    setApplySelectionRewrite(applyRewriteToEditor)
    return () => setApplySelectionRewrite(null)
  }, [editor, setApplySelectionRewrite, applyRewriteToEditor])

  // Lock editor during selection edit / active suggestion / read-only.
  // Evaluator view joins the list (spec 19): while annotation and citation
  // ranges are hidden by CSS the caret can land inside text the author cannot
  // see, so the whole document is read-only until the view is switched off.
  useEffect(() => {
    if (!editor) return
    editor.setEditable(
      canWrite &&
      !isEditingSelection &&
      !selectionEditResult &&
      !draftPatchPreview &&
      !isResolvingDraftPatchPreview &&
      !isEvaluatorView
    )
  }, [editor, canWrite, isEditingSelection, selectionEditResult, draftPatchPreview, isResolvingDraftPatchPreview, isEvaluatorView])

  // Trigger re-decoration on suggestion change
  useEffect(() => {
    if (!editor) return
    const tr = editor.state.tr.setMeta('suggestion-update', true)
    editor.view.dispatch(tr)
  }, [editor, selectionEditResult])

  // Trigger re-decoration on selection change (highlight plugin)
  useEffect(() => {
    if (!editor) return
    const tr = editor.state.tr.setMeta('selection-highlight-update', true)
    editor.view.dispatch(tr)
  }, [editor, selection])

  // Flushes the pending save now. Never call this directly — go through
  // savePendingSegments, which keeps it to one request in flight.
  const saveNow = useCallback(async (): Promise<boolean> => {
    if (!currentSessionId) return true
    if (!isDirtyRef.current) return true

    // Second half of the E2E F1 guard. A dirty flag set *before* the preview
    // arrived still points the save loop at the editor, whose document has since
    // been swapped for the diff — and the store→editor sync nulls
    // pendingContentRef, so the `pendingContentRef === null` branch would
    // re-derive the content straight from the diffed document. Hold until the
    // preview resolves; accept/reject then clears the dirty flag itself.
    if (useAgentStore.getState().draftPatchPreview) return true

    setSaveStatus('saving')

    if (isCompositeDocument) {
      if (!editor) return false

      const changedSegments = detectChangedSegments(
        editor,
        previousSegmentSnapshotRef.current
      )
      if (changedSegments.length === 0) {
        isDirtyRef.current = false
        setSaveStatus('saved')
        return true
      }

      let anyFailed = false
      for (const changed of changedSegments) {
        const latestSegment = useAgentStore
          .getState()
          .segments
          .find((segment) => segment.sectionId === changed.sectionId)

        if (!latestSegment) {
          anyFailed = true
          continue
        }

        try {
          const result = await saveAdoptingOwnWrite(
            unconfirmedSaves, changed.sectionId, changed.content, latestSegment.version,
            (expectedVersion) => saveSegment(currentSessionId, changed.sectionId, {
              content: changed.content,
              expectedVersion,
              wordCount: changed.wordCount,
            })
          )

          previousSegmentSnapshotRef.current.set(changed.sectionId, changed.content)

          isOwnSaveRef.current = true
          useAgentStore.getState().updateSegmentVersion(changed.sectionId, result.version, {
            content: changed.content,
            wordCount: result.wordCount,
            status: result.status,
          })
        } catch (err) {
          if (err instanceof ApiRequestError && err.statusCode === 409) {
            const details = (err.details ?? {}) as Partial<SegmentConflictDetails>
            setConflict({
              sectionId: changed.sectionId,
              localContent: changed.content,
              localWordCount: changed.wordCount,
              remoteVersion: details.currentVersion ?? latestSegment.version + 1,
              remoteContent: details.currentContent ?? '',
              ...resolveConflictAttribution(details.lastEditedBy, currentUserId),
            })
            isDirtyRef.current = false
            setSaveStatus('conflict')
            return false
          }
          if (err instanceof ApiRequestError && (err.code === 'ARTIFACT_FROZEN' || err.code === 'SECTION_ASSIGNED')) {
            isDirtyRef.current = false
            setSaveStatus('blocked')
            const assigneeName = (err.details?.['assigneeName'] as string | null | undefined) ?? null
            setBlockedReason(err.code === 'ARTIFACT_FROZEN'
              ? 'This artifact was finalised while you were editing. Your text can\'t be saved here — save a backup, or ask the owner to reopen the artifact.'
              : `This section was assigned to ${assigneeName ?? 'another writer'} while you were editing. Your text can't be saved here — save a backup, or ask them to unassign it.`)
            return false
          }
          anyFailed = true
        }
      }

      // Only clean if what was saved is what the editor holds now. Typing that
      // landed while the requests were out is not in them; marking the editor
      // clean left it unsent behind a "Saved" label. The snapshot holds what
      // each section was saved as, so any difference is exactly that typing —
      // unless a suggestion preview arrived meanwhile: the editor then holds
      // the diff, and the preview's accept/reject owns the next write (E2E F1).
      const editedWhileSaving = !anyFailed &&
        !useAgentStore.getState().draftPatchPreview &&
        detectChangedSegments(editor, previousSegmentSnapshotRef.current).length > 0
      isDirtyRef.current = anyFailed || editedWhileSaving
      if (anyFailed) {
        writeDraftMirror(currentSessionId, sectionKey, {
          content: editor ? getNormalizedEditorHtml(editor) : '',
          baseVersion: 0,
          savedAt: new Date().toISOString(),
        })
        setFailedSaveCount((count) => count + 1)
      } else {
        saveRetryCountRef.current = 0
        clearDraftMirror(currentSessionId, sectionKey)
      }
      setSaveStatus(anyFailed ? 'error' : editedWhileSaving ? 'unsaved' : 'saved')
      if (editedWhileSaving) scheduleAutosaveRef.current()
      return !anyFailed
    }

    if (pendingContentRef.current === null) {
      // Recover from stale dirty flags: only save if editor content actually differs.
      if (!editor) {
        isDirtyRef.current = false
        setSaveStatus('saved')
        return true
      }

      const editorContent = getNormalizedEditorHtml(editor)
      if (editorContent === previousContentRef.current) {
        isDirtyRef.current = false
        setSaveStatus('saved')
        return true
      }

      pendingContentRef.current = editorContent
      previousContentRef.current = editorContent
    }

    const content = pendingContentRef.current
    if (content === null) {
      isDirtyRef.current = false
      setSaveStatus('saved')
      return true
    }

    pendingContentRef.current = null
    isDirtyRef.current = false

    let anyFailed = false
    // Re-read the current version at flush time to avoid 409s from stale
    // captures. That includes the fallback segment (a unit-scoped session keeps
    // its one segment under another key): a follow-up save starts the moment the
    // previous one answers, before React re-renders, so `currentSegment` can
    // still carry the version that save just replaced.
    const storeSegments = useAgentStore.getState().segments
    const currentSeg = (
      storeSegments.find((s) => s.sectionId === sectionKey)
      ?? (currentSegment
        ? storeSegments.find((s) => s.sectionId === currentSegment.sectionId) ?? currentSegment
        : null)
    )
    if (!currentSeg) {
      // First save into a never-drafted section: no segment row exists yet.
      // expectedVersion 0 tells the backend to create it (Spec 08 §6.6).
      try {
        // A create that timed out may still have created the row; the retry's
        // 409 then carries our own text and is re-sent as an update.
        const result = await saveAdoptingOwnWrite(
          unconfirmedSaves, sectionKey, content, 0,
          (expectedVersion) => saveSegment(currentSessionId, sectionKey, {
            content,
            expectedVersion,
            wordCount: countWords(content),
            heading: section.title,
          })
        )
        isOwnSaveRef.current = true
        useAgentStore.getState().upsertSegment({
          id: result.id,
          sessionId: currentSessionId,
          sectionId: result.sectionId,
          heading: result.heading,
          content,
          status: result.status,
          version: result.version,
          wordCount: result.wordCount,
          editedWhileStale: false,
          coverageRefs: [],
          citationRefs: [],
          libraryCitationRefs: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      } catch {
        anyFailed = true
        // Hand the text back for the retry — unless newer typing is already
        // pending. That typing contains this text; putting this back over it
        // would retry the older copy and drop what was typed during the save.
        pendingContentRef.current ??= content
      }
    } else {
      try {
        const result = await saveAdoptingOwnWrite(
          unconfirmedSaves, currentSeg.sectionId, content, currentSeg.version,
          (expectedVersion) => saveSegment(currentSessionId, currentSeg.sectionId, {
            content,
            expectedVersion,
            wordCount: countWords(content),
          })
        )
        isOwnSaveRef.current = true
        useAgentStore.getState().updateSegmentVersion(currentSeg.sectionId, result.version, {
          content,
          wordCount: result.wordCount,
          status: result.status,
        })
      } catch (err) {
        if (err instanceof ApiRequestError && err.statusCode === 409) {
          const details = (err.details ?? {}) as Partial<SegmentConflictDetails>
          setConflict({
            sectionId: currentSeg.sectionId,
            localContent: content,
            localWordCount: countWords(content),
            remoteVersion: details.currentVersion ?? currentSeg.version + 1,
            remoteContent: details.currentContent ?? '',
            ...resolveConflictAttribution(details.lastEditedBy, currentUserId),
          })
          isDirtyRef.current = false
          pendingContentRef.current = null
          setSaveStatus('conflict')
          return false
        }
        if (err instanceof ApiRequestError && (err.code === 'ARTIFACT_FROZEN' || err.code === 'SECTION_ASSIGNED')) {
          isDirtyRef.current = false
          pendingContentRef.current = null
          setSaveStatus('blocked')
          const assigneeName = (err.details?.['assigneeName'] as string | null | undefined) ?? null
          setBlockedReason(err.code === 'ARTIFACT_FROZEN'
            ? 'This artifact was finalised while you were editing. Your text can\'t be saved here — save a backup, or ask the owner to reopen the artifact.'
            : `This section was assigned to ${assigneeName ?? 'another writer'} while you were editing. Your text can't be saved here — save a backup, or ask them to unassign it.`)
          return false
        }
        anyFailed = true
        // As above: never put the older text back over newer pending typing.
        pendingContentRef.current ??= content
      }
    }

    // Typing that landed while the request was out set pendingContentRef again
    // (it was nulled before the await). That text is not in this save, so stay
    // dirty and let autosave send it; marking the editor clean here left it
    // unsent behind a "Saved" label. Typing that ended where it started (a
    // letter typed and deleted) is already saved.
    if (!anyFailed && pendingContentRef.current === content) pendingContentRef.current = null
    const editedWhileSaving = !anyFailed && pendingContentRef.current !== null
    isDirtyRef.current = anyFailed || editedWhileSaving
    if (anyFailed) {
      writeDraftMirror(currentSessionId, sectionKey, {
        content: pendingContentRef.current ?? content,
        baseVersion: currentSeg?.version ?? 0,
        savedAt: new Date().toISOString(),
      })
      setFailedSaveCount((count) => count + 1)
    } else {
      saveRetryCountRef.current = 0
      clearDraftMirror(currentSessionId, sectionKey)
    }
    setSaveStatus(anyFailed ? 'error' : editedWhileSaving ? 'unsaved' : 'saved')
    if (editedWhileSaving) scheduleAutosaveRef.current()
    return !anyFailed
  }, [
    currentSessionId,
    sectionKey,
    currentSegment,
    isCompositeDocument,
    editor,
    currentUserId,
    unconfirmedSaves,
  ])

  // The queue calls back through this ref, so a follow-up save always runs the
  // latest render's saveNow rather than the one captured when it was requested.
  const saveNowRef = useRef(saveNow)
  saveNowRef.current = saveNow

  // Every save — debounce, retry, Retry button, flush before a section switch
  // or AI turn — goes through here, so at most one request is in flight. A save
  // requested meanwhile runs once that one answers, with the latest text and
  // the version it returned. Awaiting it therefore also waits out a save that
  // is already in flight.
  const savePendingSegments = useCallback(
    (): Promise<boolean> => saveQueue.run(() => saveNowRef.current()),
    [saveQueue]
  )

  const clearSaveTimer = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = null
  }, [])

  const startSaveTimer = useCallback((delay: number) => {
    const timer = setTimeout(() => {
      if (saveTimeoutRef.current === timer) saveTimeoutRef.current = null
      void savePendingSegments()
    }, delay)
    saveTimeoutRef.current = timer
  }, [savePendingSegments])

  autosaveSuspendedRef.current = conflict !== null || blockedReason !== null

  // Arm the autosave debounce unless a timer is already pending — a pending
  // timer saves whatever is current when it fires, so it covers this edit too
  // (and a pending retry is swapped for the debounce by the effect below, as
  // the edit moves the status off 'error'). Called by every edit, and by a
  // save that answers while the editor is still dirty, so autosave never waits
  // on a *change* of save status. It must not: the status can read 'unsaved'
  // from one keystroke to the next. A keystroke landing as the timer fires is
  // rendered together with that save's 'saving', which is then never shown, so
  // an effect keyed on the status never runs again. On dev this stopped
  // autosave for good during fast typing (browser check on bfd3dc70).
  const scheduleAutosave = useCallback(() => {
    if (!isMountedRef.current || autosaveSuspendedRef.current) return
    if (saveTimeoutRef.current) return
    startSaveTimer(AUTOSAVE_DEBOUNCE_MS)
  }, [startSaveTimer])
  scheduleAutosaveRef.current = scheduleAutosave

  // On close, a pending autosave timer and any save still queued behind an
  // in-flight one are dropped: no writes from an editor that is no longer on
  // screen. The in-flight save itself finishes, and cannot re-arm the timer.
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      clearSaveTimer()
      saveQueue.cancelPending()
    }
  }, [saveQueue, clearSaveTimer])

  // Status-driven timer: the debounce when an edit moves the status to
  // 'unsaved', the retry backoff after a failure, nothing during a conflict or
  // a block. Re-planned after every failed save (failedSaveCount) as well as
  // on every status change.
  useEffect(() => {
    clearSaveTimer()
    const plan = planAutosaveTimer(saveStatus, saveRetryCountRef.current, conflict !== null, blockedReason !== null)
    if (plan.kind === 'debounce') {
      saveRetryCountRef.current = 0
      setRetriesExhausted(false)
      startSaveTimer(plan.delay)
    } else if (plan.kind === 'retry') {
      setRetriesExhausted(false)
      saveRetryCountRef.current = plan.nextCount
      startSaveTimer(plan.delay)
    } else if (plan.kind === 'exhausted') {
      setRetriesExhausted(true)
    }
    return clearSaveTimer
  }, [saveStatus, failedSaveCount, clearSaveTimer, startSaveTimer, conflict, blockedReason])

  const handleRetrySave = useCallback(() => {
    saveRetryCountRef.current = 0
    setRetriesExhausted(false)
    void savePendingSegments()
  }, [savePendingSegments])

  // WF4: warn before unload while the draft has unpersisted work. In-app
  // navigation flushes via flushPendingSavesBeforeThreadChange (pinned by test),
  // so this only guards a hard browser close/reload — no useBlocker needed.
  useEffect(() => {
    if (!shouldGuardBeforeUnload(saveStatus)) return
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [saveStatus])

  // WF4: on mount, offer to restore a locally-mirrored save that never reached
  // the server (flaky save + tab close). Re-evaluated per session/section.
  useEffect(() => {
    if (!currentSessionId) {
      setMirrorBanner(null)
      return
    }
    const mirror = readDraftMirror(currentSessionId, sectionKey)
    const serverContent = currentSegment?.content ?? ''
    setMirrorBanner(mirror && mirror.content !== serverContent ? mirror : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, sectionKey])

  // Reset the one-click-backup confirmation when the editor leaves the blocked state.
  useEffect(() => {
    if (!blockedReason) setBackupDone(false)
  }, [blockedReason])

  const handleRestoreMirror = useCallback(() => {
    if (!mirrorBanner) return
    if (editor) {
      editor.commands.setContent(mirrorBanner.content)
      previousContentRef.current = mirrorBanner.content
    }
    pendingContentRef.current = mirrorBanner.content
    isDirtyRef.current = true
    setSaveStatus('unsaved')
    scheduleAutosaveRef.current()
    setMirrorBanner(null)
  }, [mirrorBanner, editor])

  const handleDiscardMirror = useCallback(() => {
    if (currentSessionId) clearDraftMirror(currentSessionId, sectionKey)
    setMirrorBanner(null)
  }, [currentSessionId, sectionKey])

  // A composite document holds every segment at once and `sectionKey` is
  // '__document__' — a key nothing is ever written under, because the drafting
  // writers key segments either by outline sectionKey or by plan heading-slug.
  // Segment-scoped calls (version history, restore) must therefore address the
  // segment the caret sits in, or they operate on a row that does not exist.
  const resolveSegmentScopedTarget = useCallback((): { sectionId: string; heading: string | null } => {
    if (!isCompositeDocument) {
      return { sectionId: persistedSectionId, heading: currentSegment?.heading ?? null }
    }
    const caretSectionId = editor ? getSelectionSegmentId(editor) : null
    const target = orderedSegments.find((s) => s.sectionId === caretSectionId)
      ?? orderedSegments[0]
    return target
      ? { sectionId: target.sectionId, heading: target.heading }
      : { sectionId: persistedSectionId, heading: null }
  }, [isCompositeDocument, persistedSectionId, currentSegment, editor, orderedSegments])

  const handleSaveBackup = useCallback(async () => {
    if (!currentSessionId) return

    // In a composite document the editor's HTML spans every segment, so backing
    // it up under one key would both address a nonexistent row and conflate
    // sections. Split it and file each segment against its own key so no
    // section's unsaved work is dropped.
    if (isCompositeDocument && editor) {
      const extracted = extractSegmentsFromEditor(editor)
      if (extracted.size > 0) {
        const storeSegments = useAgentStore.getState().segments
        let anyFailed = false
        for (const [segmentSectionId, segmentContent] of extracted) {
          const segment = storeSegments.find((s) => s.sectionId === segmentSectionId)
          if (!segment) {
            anyFailed = true
            continue
          }
          try {
            await backupSegmentContent(
              currentSessionId, segmentSectionId, segmentContent, segment.version, 'manual'
            )
          } catch {
            anyFailed = true
          }
        }
        if (anyFailed) {
          toast.error('Could not save a backup. Please try again.')
          return
        }
        setBackupDone(true)
        return
      }
    }

    const content = editor ? getNormalizedEditorHtml(editor) : (pendingContentRef.current ?? '')
    const baseVersion = currentSegment?.version ?? 0
    try {
      await backupSegmentContent(currentSessionId, persistedSectionId, content, baseVersion, 'manual')
      setBackupDone(true)
    } catch {
      toast.error('Could not save a backup. Please try again.')
    }
  }, [currentSessionId, persistedSectionId, editor, currentSegment, isCompositeDocument])

  const handleConflictKeepMine = useCallback(async () => {
    if (!conflict || !currentSessionId) return
    try {
      const result = await saveSegment(currentSessionId, conflict.sectionId, {
        content: conflict.localContent,
        expectedVersion: conflict.remoteVersion,
        wordCount: conflict.localWordCount,
      })
      isOwnSaveRef.current = true
      useAgentStore.getState().updateSegmentVersion(conflict.sectionId, result.version, {
        content: conflict.localContent,
        wordCount: result.wordCount,
        status: result.status,
      })
      if (isCompositeDocument) {
        previousSegmentSnapshotRef.current.set(conflict.sectionId, conflict.localContent)
      }
      setConflict(null)
      if (isCompositeDocument) {
        // Other sections edited in the same window still need saving — the save
        // loop bailed at this section's 409 before reaching them. Re-arm autosave
        // (the gate !conflict && !blockedReason is satisfied now that conflict is
        // cleared) so it re-diffs and flushes the remaining changed sections.
        isDirtyRef.current = true
        setSaveStatus('unsaved')
      } else {
        setSaveStatus('saved')
      }
    } catch {
      toast.error('Could not save your version. Please try again.')
    }
  }, [conflict, currentSessionId, isCompositeDocument])

  const handleConflictLoadTheirs = useCallback(async () => {
    if (!conflict || !currentSessionId) return
    try {
      await backupSegmentContent(
        currentSessionId, conflict.sectionId, conflict.localContent, conflict.remoteVersion
      )
    } catch {
      // Backup failed — do NOT discard the user's local work. Leave the dialog
      // open with content and conflict state untouched so they can retry.
      toast.error('Could not back up your edits — nothing was changed. Please try again.')
      return
    }

    if (isCompositeDocument) {
      // One section of many — re-init the whole session to pull the fresh
      // document. The store→editor sync effect then reassembles the composite
      // document and rebuilds previousSegmentSnapshotRef (isOwnSaveRef stays
      // false so the reassembly runs), so the next save diffs cleanly.
      isDirtyRef.current = false
      const context = useAgentStore.getState().sessionInitContext ?? sessionInitContext
      if (context) {
        try {
          await initSession(
            context.bidId,
            context.artifactId,
            context.sectionKey,
            context.scopeKind ?? 'unit'
          )
        } catch {
          // Re-init is best-effort.
        }
      }
      setConflict(null)
      // Other sections edited in the same window still need saving — re-arm
      // autosave so the next diff pass flushes the remaining changed sections.
      isDirtyRef.current = true
      setSaveStatus('unsaved')
      toast.success('Your edits were saved to version history.')
      return
    }

    if (!editor) return
    editor.commands.setContent(conflict.remoteContent)
    previousContentRef.current = conflict.remoteContent
    isOwnSaveRef.current = true
    useAgentStore.getState().updateSegmentVersion(conflict.sectionId, conflict.remoteVersion, {
      content: conflict.remoteContent,
      wordCount: countWords(conflict.remoteContent),
      status: 'fresh',
    })
    setConflict(null)
    setSaveStatus('saved')
    toast.success('Your edits were saved to version history.')
  }, [conflict, currentSessionId, editor, isCompositeDocument, initSession, sessionInitContext])

  const handleRestored = useCallback(({ version, content, wordCount }: { version: number; content: string; wordCount: number }) => {
    const targetSectionId = versionHistoryTarget?.sectionId ?? persistedSectionId
    // Version rows keep whatever format wrote them — AI drafts are Markdown,
    // editor saves are HTML. Written in raw, a restored AI draft would land in
    // the document as literal "## Heading" text. Idempotent for HTML input.
    const html = normalizeRichTextForEditor(content)

    if (isCompositeDocument) {
      // The restored content is ONE segment of the composite document, so it must
      // not be written straight into the editor — that would replace every other
      // section with it. Update the store and let the store→editor sync effect
      // reassemble the document (isOwnSaveRef deliberately left false so it runs).
      pendingContentRef.current = null
      isDirtyRef.current = false
      useAgentStore.getState().updateSegmentVersion(targetSectionId, version, {
        content: html, wordCount, status: 'fresh',
      })
      setSaveStatus('saved')
      return
    }

    if (!editor) return
    editor.commands.setContent(html)
    previousContentRef.current = html
    pendingContentRef.current = null
    isDirtyRef.current = false
    isOwnSaveRef.current = true
    useAgentStore.getState().updateSegmentVersion(targetSectionId, version, {
      content: html, wordCount, status: 'fresh',
    })
    setSaveStatus('saved')
    setWordCount(wordCount)
  }, [editor, persistedSectionId, versionHistoryTarget, isCompositeDocument])

  const flushPendingSavesBeforeThreadChange = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }

    const saved = await savePendingSegments()
    if (!saved) {
      throw new Error('Could not save draft changes. Resolve save errors before switching chats.')
    }
  }, [savePendingSegments])

  useEffect(() => {
    setThreadTransitionSaveFlusher(flushPendingSavesBeforeThreadChange)
    return () => setThreadTransitionSaveFlusher(null)
  }, [setThreadTransitionSaveFlusher, flushPendingSavesBeforeThreadChange])

  const handleAccept = useCallback(() => {
    if (!selectionEditResult) return
    try {
      let { from, to } = selectionEditResult.selectionRange
      // The offsets were captured when the text was selected. If the document
      // has moved since, applying them blind pastes the rewrite over unrelated
      // prose — so re-locate the text, and refuse if it cannot be placed.
      const sourceText = selectionEditResult.sourceText
      const doc = editor?.state.doc as RewriteScanNode | undefined
      if (sourceText && doc) {
        const resolved = resolveSelectionRewriteRange(doc, { from, to }, sourceText)
        if (!resolved) {
          toast.error('The document changed since this rewrite was suggested. Re-select the text and try again.')
          return
        }
        from = resolved.from
        to = resolved.to
      }
      const applyRewrite = applySelectionRewrite ?? applyRewriteToEditor
      applyRewrite(from, to, selectionEditResult.replacementText)
    } catch {
      toast.error('Could not apply AI suggestion. Please retry.')
    } finally {
      clearSelectionEdit()
    }
  }, [selectionEditResult, applySelectionRewrite, applyRewriteToEditor, clearSelectionEdit, editor])

  const handleReject = useCallback(() => { clearSelectionEdit() }, [clearSelectionEdit])

  const resetLocalDirtyStateAfterPreviewResolution = useCallback(() => {
    isDirtyRef.current = false
    pendingContentRef.current = null
    if (isCompositeDocument && editor) {
      previousSegmentSnapshotRef.current = extractSegmentsFromEditor(editor)
    }
    setSaveStatus('saved')
  }, [editor, isCompositeDocument])

  const handleAcceptDraftPatchPreview = useCallback(async () => {
    await acceptDraftPatchPreview()
    resetLocalDirtyStateAfterPreviewResolution()
  }, [acceptDraftPatchPreview, resetLocalDirtyStateAfterPreviewResolution])

  const handleRejectDraftPatchPreview = useCallback(async () => {
    await rejectDraftPatchPreview()
    resetLocalDirtyStateAfterPreviewResolution()
  }, [rejectDraftPatchPreview, resetLocalDirtyStateAfterPreviewResolution])

  // Escape rejects only when nothing else claimed it. Radix menus, dialogs and
  // popovers close on Escape in a document capture listener and call
  // preventDefault(), which runs before these window listeners. Without the
  // check, closing "More actions" also discarded the pending suggestion.
  useEffect(() => {
    if (!selectionEditResult) return
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); handleAccept() }
      else if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); handleReject() }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [selectionEditResult, handleAccept, handleReject])

  useEffect(() => {
    if (!draftPatchPreview || isResolvingDraftPatchPreview) return
    const onKeyDown = (e: KeyboardEvent) => {
      // An outdated suggestion cannot be accepted; Escape still dismisses it.
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); if (!draftPatchPreview.outdated) void handleAcceptDraftPatchPreview() }
      else if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); void handleRejectDraftPatchPreview() }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [draftPatchPreview, isResolvingDraftPatchPreview, handleAcceptDraftPatchPreview, handleRejectDraftPatchPreview])

  const runExportSection = useCallback(async () => {
    if (!artifactId || !sectionKey) return
    setIsExporting(true)
    try {
      const { pendingAiChanges } = await exportArtifactToDocx(artifactId, sectionKey)
      toastExportResult('Section exported successfully', pendingAiChanges)
    } catch (err) {
      toastApiError(err, 'Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }, [artifactId, sectionKey])

  /**
   * "Review gaps" from the export dialog: close it and put the caret on the
   * first gap marker in the document.
   *
   * The scan walks text nodes, so a token split across marks (a bolded word
   * mid-payload) will not be found and the jump is skipped — the dialog still
   * listed the gap, which is what the author needed.
   */
  const jumpToFirstGap = useCallback(() => {
    if (!editor) return
    let target: number | null = null
    editor.state.doc.descendants((node, nodePos) => {
      if (target !== null) return false
      if (!node.isText || !node.text) return true
      const offset = node.text.indexOf('[TODO:')
      if (offset !== -1) {
        target = nodePos + offset
        return false
      }
      return true
    })
    if (target === null) return
    editor.chain().focus().setTextSelection(target).scrollIntoView().run()
  }, [editor])

  // Spec 19: same pre-export confirmation as the overview pages, but scanned
  // locally — the editor already holds the content, so there is no report call.
  //
  // NOTE: this counts RAW tokens via `findAnnotations`; the per-heading gap
  // counts in the header count only tokens the decoration plugin managed to
  // CHIP, which needs parsed heading boundaries and skips Markdown-bodied
  // segments. This number is the authoritative one for export and may
  // legitimately exceed the header's.
  const [exportGapScan, setExportGapScan] = useState<LocalGapScan | null>(null)

  const handleExportSection = useCallback(async () => {
    if (!artifactId || !sectionKey) return
    const scan = scanContentForGaps(editor?.getHTML() ?? currentSegment?.content ?? '')
    if (scan.todos.length > 0 || scan.malformedCount > 0) {
      setExportGapScan(scan)
      return
    }
    await runExportSection()
  }, [artifactId, sectionKey, editor, currentSegment?.content, runExportSection])

  const targetWordCount = section.targetWordCount
  const wordCountDisplay = targetWordCount
    ? `${wordCount.toLocaleString()} / ${targetWordCount.toLocaleString()} words`
    : `${wordCount.toLocaleString()} words`

  // Review flow local state
  const [reviewComment, setReviewComment] = useState('')
  // Spec 29 §7.3 — rides the change request; default off.
  const [requireReReview, setRequireReReview] = useState(false)
  const [showReviewInput, setShowReviewInput] = useState(false)

  // Eligible reviewers: bid members excluding the section writer
  const writerUserId = currentUserId // current user is the writer when canWrite=true
  // The planned reviewer (if eligible) goes first; the rest keep their order.
  const eligibleReviewers = (bidMembers ?? [])
    .filter((m) => m.userId !== writerUserId && m.role !== 'viewer')
    .sort((a, b) => Number(b.userId === plannedReviewerId) - Number(a.userId === plannedReviewerId))

  const renderReviewerItems = (requestReview: (reviewerId: string) => void) =>
    eligibleReviewers.map((m) => {
      const color = getAvatarColor(m.userId)
      const isPlanned = !!plannedReviewerId && m.userId === plannedReviewerId
      return (
        <DropdownMenuItem key={m.userId} onClick={() => requestReview(m.userId)}>
          <div className="flex w-full min-w-0 items-center gap-2">
            <div className={cn('h-4 w-4 rounded-full flex items-center justify-center shrink-0 overflow-hidden', m.avatarUrl ? 'bg-muted' : color.bg)}>
              {m.avatarUrl ? (
                <img src={m.avatarUrl} alt={m.fullName ?? 'User'} className="h-4 w-4 rounded-full object-cover" />
              ) : (
                <span className={cn('text-[8px] font-medium', color.text)}>
                  {getInitials(m.fullName)}
                </span>
              )}
            </div>
            <span className="truncate text-sm">{m.fullName || m.email}</span>
            {isPlanned && <span className="text-meta ml-auto shrink-0">Planned</span>}
          </div>
        </DropdownMenuItem>
      )
    })

  // Spec 29 §7.3. The reviewer held the section open, so Complete is off the
  // table for the writer and the review button becomes the primary way out.
  // The server enforces the same rule for non-owners.
  const mustReReview =
    latestReviewRound?.decision === 'changes_requested' && latestReviewRound.requireReReview === true

  const renderStatusPill = () => {
    // `whitespace-nowrap` is load-bearing, not cosmetic. Without it the pill's
    // label ("Draft complete") is wrappable, so the header's leading group
    // reports a min-content that assumes the label breaks across two lines —
    // smaller than the width the pill actually occupies, because its wrapper is
    // `shrink-0` and never lets it wrap. Flexbox then sized the group from that
    // under-counted min-content and the pill + Brief spilled out of it, painting
    // over the action group (F24). Pinning the label to one line makes the
    // group's min-content match its real laid-out width, so the row wraps
    // instead of overlapping.
    return <SectionStatusPill status={collaborationStatus ?? null} />
  }

  return (
    <div className="flex flex-col h-full" data-drafteditor="true">
      {/* Document chrome header — optional back/title + status on the left,
          primary actions + export on the right.

          Wrapping is load-bearing. The leading group deliberately does NOT
          carry `min-w-0`: with it, the group shrank below its own content
          while its shrink-0 children (status pill, Brief) kept full width and
          spilled out of the box, painting over the action group — the ≤1600px
          collision. Keeping the group's min-content width instead makes the
          row wrap, so the actions drop to a second line rather than overlap.
          The title is the one flexible part; `truncate` lets it give way
          first, so the header only wraps once truncation isn't enough.

          `flex-1` (basis 0) is deliberate — do NOT "fix" it to `basis-auto
          grow`. Basis 0 is still clamped by the group's automatic minimum size
          (`min-width: auto` -> min-content), so line-breaking and layout share
          a single threshold. That equality is the whole safety property here,
          and it only holds while every shrink-0 child reports a min-content
          equal to its laid-out width — which is why the status pill must keep
          `whitespace-nowrap` (see renderStatusPill). A wrappable label breaks
          the equality and reopens F24: the row stops wrapping while the group
          is sized below its real content, and the pill + Brief spill over the
          actions. Verified live at a 585.47px header: wraps to two rows,
          Brief stays hit-testable.

          Leading order: back, title, status pill, the host's slot (Brief,
          plus whatever else the host page adds). Every one of those after the
          title is `shrink-0` with a `whitespace-nowrap` label, for the reason
          above. The trailing group is the Preview toggle, the ⋯ menu
          (Preview, Export as Word), then the status-dependent primary
          button(s), last. */}
      <div
        className="flex flex-wrap items-center gap-x-2 gap-y-1 min-h-11 py-1.5 px-4 border-b"
        data-editor-header="true"
      >
        <div className="flex items-center gap-3 flex-1" data-header-leading="true">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={onBack}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          {headerTitle && (
            <h1 className="text-sm font-medium truncate min-w-0">{headerTitle}</h1>
          )}
          <div className="flex items-center shrink-0">{renderStatusPill()}</div>
          {headerStatusSlot && <div className="flex items-center shrink-0">{headerStatusSlot}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {/* Evaluator view — spec 19. Lives in the chrome header, not
              EditorToolbar: the toolbar only renders for writers, and a
              reviewer on a finalised draft is exactly who needs to read the
              response close to how the evaluator will see it.

              The visible Preview button is the main way in. Its pressed wash
              is also what says the mode is on, and clicking it again turns it
              off — so it must stay on screen (`shrink-0`, one-line label).
              The ⋯ menu keeps a checkbox item for the same mode. Same look as
              the Full response reader's Preview button.

              The copy says the section "as it will be exported", not
              "exactly": spacing matches the export for the common cases, but
              a hidden token followed by another non-marker hidden token
              ("x [1][TODO: y].") still leaves a space before the full stop. */}
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'shrink-0 whitespace-nowrap',
              // Matches the reader's Preview button: muted at rest, the active
              // wash when on.
              isEvaluatorView ? 'bg-foreground/[0.07] text-foreground font-medium' : 'text-muted-foreground'
            )}
            aria-pressed={isEvaluatorView}
            onClick={() => setIsEvaluatorView((v) => !v)}
            title="Shows the section as it will be exported"
          >
            <Eye className="h-4 w-4 mr-1.5" />
            Preview
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="More actions"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MoreHorizontal className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuCheckboxItem
                checked={isEvaluatorView}
                onCheckedChange={(checked) => setIsEvaluatorView(checked === true)}
                title="Shows the section as it will be exported"
              >
                Preview
              </DropdownMenuCheckboxItem>
              {/* Export is a POST, which the backend refuses a view-only user. */}
              {!isViewOnly && (
                <DropdownMenuItem
                  disabled={isExporting || !currentSegment}
                  onSelect={() => void handleExportSection()}
                  className="pl-8"
                >
                  {isExporting ? (
                    <Loader2 className="absolute left-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="absolute left-2 h-4 w-4" />
                  )}
                  Export as Word
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {canWrite && onCollaborationStatusChange && collaborationStatus === 'in_progress' && (
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => onCollaborationStatusChange('draft_complete')}
            >
              <FileCheck className="h-3 w-3 mr-1" />
              Mark draft complete
            </Button>
          )}

          {canWrite && collaborationStatus === 'draft_complete' && (
            <>
              {onRequestReview && eligibleReviewers.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" className="h-7 text-xs">
                      <Send className="h-3 w-3 mr-1" />
                      Submit for review
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {renderReviewerItems(onRequestReview)}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {onCollaborationStatusChange && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onCollaborationStatusChange('complete')}
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Mark complete
                </Button>
              )}
            </>
          )}

          {canWrite && collaborationStatus === 'changes_requested' && (
            <>
              {onRequestReview && eligibleReviewers.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" className="h-7 text-xs">
                      <RotateCcw className="h-3 w-3 mr-1" />
                      {mustReReview ? 'Submit changes for review' : 'Request another review'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {renderReviewerItems(onRequestReview)}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {onCollaborationStatusChange && !mustReReview && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onCollaborationStatusChange('complete')}
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Mark complete
                </Button>
              )}
            </>
          )}

        </div>
      </div>

      {/* Locked banner for non-writers */}
      {!canWrite && !isReviewer ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-[hsl(var(--status-warning)/0.08)] border-b border-[hsl(var(--status-warning)/0.35)]">
          <Lock className="h-4 w-4 text-[hsl(var(--status-warning))] shrink-0" />
          <span className="text-sm text-foreground">
            {bidAccess.isReadOnly
              ? bidAccess.message
              : lockedReason
              ? `${lockedReason}. View only.`
              : lockedByUser
                ? `This section is assigned to ${lockedByUser}. View only.`
                : 'This section is read-only.'}
          </span>
        </div>
      ) : !canWrite && isReviewer ? (
        /* Reviewer banner */
        null /* Handled below as a separate banner */
      ) : (
        <EditorToolbar
          editor={editor}
          // `isEvaluatorView` has to be listed explicitly: setEditable(false)
          // stops keyboard input, but a TipTap command dispatched from a
          // toolbar click runs regardless of `editable` — that is why Insert
          // Image already carries its own `|| !editor.isEditable`. Without this,
          // Bold or Insert Table would mutate ranges the author cannot see.
          disabled={isEditingSelection || !!selectionEditResult || isEvaluatorView}
          onInsertImage={triggerImagePicker}
          // Version restore targets a single section. The composite editor holds
          // all of them, so it resolves the caret's segment at open time and the
          // dialog names which section it is showing.
          onOpenVersionHistory={
            currentSessionId
              ? () => {
                  setVersionHistoryTarget(resolveSegmentScopedTarget())
                  setVersionHistoryOpen(true)
                }
              : undefined
          }
        />
      )}
      {belowToolbarSlot}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={onImageFilePicked}
      />

      {/* Reviewer banner — shown when this user is the designated reviewer */}
      {isReviewer && collaborationStatus === 'in_review' && (
        // A colleague is blocked on this Approve, so it keeps the accent — as
        // an edge, not a fill. No AI is involved; the accent marks "act here",
        // which this is.
        <div className={cn(NOTICE_ACT, 'flex items-center justify-between rounded-none border-x-0 border-t-0')}>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm">
              {writerName ?? 'The writer'} requested your review
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Popover
              open={showReviewInput}
              onOpenChange={(open) => {
                setShowReviewInput(open)
                if (!open) {
                  setReviewComment('')
                  setRequireReReview(false)
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-meta hover:text-[hsl(var(--status-warning))] hover:bg-[hsl(var(--status-warning)/0.08)]"
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Request Changes
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-3">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium">Request changes</span>
                  <Textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Optional summary — put the specifics in comments on the text."
                    rows={4}
                    className="text-sm resize-none"
                    autoFocus
                  />
                  {/* Spec 29 §7.3 — the reviewer can hold the section open until
                      it comes back to them. Default off: most change requests
                      are trusted to the writer to close out. */}
                  <label className="flex items-start gap-2 text-sm">
                    <Checkbox
                      checked={requireReReview}
                      onCheckedChange={(v) => setRequireReReview(v === true)}
                      aria-label="Require another review before completion"
                      className="mt-0.5"
                    />
                    <span>
                      Require another review before completion
                      <span className="block text-meta">The writer must send it back to you before it can be marked complete.</span>
                    </span>
                  </label>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => {
                        setShowReviewInput(false)
                        setReviewComment('')
                        setRequireReReview(false)
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => {
                        onSubmitReview?.('changes_requested', reviewComment || undefined, { requireReReview })
                        setReviewComment('')
                        setRequireReReview(false)
                        setShowReviewInput(false)
                      }}
                    >
                      <Send className="h-3 w-3 mr-1" />
                      Send
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => onSubmitReview?.('approved')}
            >
              <Check className="h-3 w-3 mr-1" />
              Approve
            </Button>
          </div>
        </div>
      )}

      {/* Changes requested feedback banner — shown to the writer */}
      {canWrite && collaborationStatus === 'changes_requested' && latestReviewRound && latestReviewRound.decision === 'changes_requested' && (
        <div className="flex items-start gap-2 px-3 py-2 bg-[hsl(var(--status-warning)/0.08)] border-b border-[hsl(var(--status-warning)/0.35)]">
          <MessageSquare className="h-4 w-4 text-[hsl(var(--status-warning))] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-foreground font-medium">
              {latestReviewRound.reviewerName ?? 'Reviewer'} requested changes
            </span>
            {latestReviewRound.comment && (
              <p className="text-meta mt-1">{latestReviewRound.comment}</p>
            )}
          </div>
        </div>
      )}

      <StaleSegmentBanner
        staleSegments={staleSegments}
        onRegenerate={handleRegenerateStale}
        isTurnBusy={isRegenerateBusy}
      />

      {mirrorBanner && (
        <DraftMirrorRestoreBanner onRestore={handleRestoreMirror} onDiscard={handleDiscardMirror} />
      )}

      {blockedReason && (
        <BlockedEditorBanner
          message={blockedReason}
          backupDone={backupDone}
          onBackup={() => void handleSaveBackup()}
        />
      )}

      <EditorAppliedEditNotice visibleSegments={isCompositeDocument ? orderedSegments : (currentSegment ? [currentSegment] : [])} />

      {draftPatchPreview && isPreviewOutdated && (
        <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-b bg-muted/40" data-testid="suggestion-outdated">
          <span className="text-meta min-w-0">
            <span className="font-medium text-foreground">
              Suggested changes{patchSectionName ? ` — ${patchSectionName}` : ''}
            </span>
            {' · '}
            {SUGGESTION_OUTDATED}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs shrink-0"
            onClick={() => void handleRejectDraftPatchPreview()}
            disabled={isResolvingDraftPatchPreview}
          >
            {isResolvingDraftPatchPreview ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
            {SUGGESTION_DISMISS}
          </Button>
        </div>
      )}

      {draftPatchPreview && diffHtml && (
        <>
          <div
            className="flex items-center justify-between gap-3 px-3 py-1.5 border-b bg-muted/40"
            data-testid="suggestion-strip"
            data-mode={isStagedPreview(draftPatchPreview) ? 'staged' : 'applied'}
          >
            <div className="flex items-center gap-2 text-meta min-w-0">
              <span className="font-medium text-foreground truncate">
                Suggested changes{patchSectionName ? ` — ${patchSectionName}` : ''}
              </span>
              {diffStats && (diffStats.added > 0 || diffStats.removed > 0) && (
                <span className="flex items-center gap-1.5 shrink-0">
                  {diffStats.added > 0 && <span className="text-[hsl(var(--status-success))]">+{diffStats.added}</span>}
                  {diffStats.removed > 0 && <span className="text-destructive">−{diffStats.removed}</span>}
                  <span className="text-muted-foreground">words</span>
                </span>
              )}
              <span className="hidden md:inline text-muted-foreground shrink-0">· ⌘↵ Accept · Esc Reject</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => void handleRejectDraftPatchPreview()}
                disabled={isResolvingDraftPatchPreview}
              >
                <X className="h-3 w-3 mr-1" /> Reject
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={() => void handleAcceptDraftPatchPreview()}
                disabled={isResolvingDraftPatchPreview}
              >
                {isResolvingDraftPatchPreview ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                Accept
              </Button>
            </div>
          </div>
          {/* The reviewer's notes on a document job's suggestion (draft page redesign). */}
          <JobSuggestionReviewNotes artifactId={artifactId} suggestion={draftPatchPreview} />
          {!isPatchTargetVisible && (
            <div className="shrink-0 border-b bg-muted/20 px-3 py-1.5 text-meta">
              This change targets {patchSectionName ?? 'another section'} which isn't open in this editor. Open that section to review it, or accept or reject the whole change here.
            </div>
          )}
          {isPatchTargetVisible && segmentDiffData && segmentDiffData.hunks.length > 1 && (
            segmentDiffData.hunks.length <= HUNK_LIST_MAX ? (
              <HunkDecisionPanel
                hunks={segmentDiffData.hunks}
                decisions={draftPatchPreview.hunkDecisions ?? {}}
                onDecide={setHunkDecision}
                disabled={isResolvingDraftPatchPreview}
              />
            ) : (
              <div className="shrink-0 border-b bg-muted/20 px-3 py-1.5 text-meta">
                {segmentDiffData.hunks.length} changes — review them in the document below, then accept or reject the whole set.
              </div>
            )
          )}
        </>
      )}

      {selectionEditResult && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted border-b">
          <span className="text-meta">AI suggestion — review changes in editor</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleReject}><X className="h-3 w-3 mr-1" /> Reject</Button>
            <Button size="sm" className="h-7 text-xs" onClick={handleAccept}><Check className="h-3 w-3 mr-1" /> Accept</Button>
          </div>
        </div>
      )}

      <div
        className={cn(
          'flex-1 overflow-auto scrollbar-thin-visible',
          isEvaluatorView && 'evaluator-view'
        )}
        onMouseDown={() => { isMouseDownRef.current = true }}
      >
        <EditorContent editor={editor} className="h-full" />
        <CitationTooltip
          state={citationTooltip}
          onClose={() => setCitationTooltip(null)}
          citationRefsBySectionId={citationRefsBySectionId}
          fallbackCitationRefs={fallbackCitationRefs}
          libraryCitationRefsBySectionId={libraryCitationRefsBySectionId}
          fallbackLibraryCitationRefs={fallbackLibraryCitationRefs}
        />
        {editor && (bidId ?? sessionInitContext?.bidId) && (
          <CommentBubbleButton
            bidId={(bidId ?? sessionInitContext?.bidId)!}
            artifactId={artifactId}
            bidMembers={bidMembers}
            resolveSelection={() => {
              if (!editor) return null
              const { from, to } = editor.state.selection
              if (from === to) return null
              const sectionId = isCompositeDocument
                ? getSelectionSegmentId(editor)
                : sectionKey
              if (!sectionId) return null
              const quotedText = editor.state.doc.textBetween(from, to, ' ')
              if (!quotedText.trim()) return null
              const truncated = quotedText.length > 300
                ? `${quotedText.slice(0, 300)}`
                : quotedText
              return {
                sectionId,
                startPos: from,
                endPos: to,
                quotedText: truncated,
              }
            }}
            isCrossSegment={() =>
              !!editor && isCompositeDocument && isSelectionCrossSegment(editor)
            }
            getSelectionRect={() => {
              if (!editor) return null
              const { from, to } = editor.state.selection
              if (from === to) return null
              try {
                const start = editor.view.coordsAtPos(from)
                const end = editor.view.coordsAtPos(to)
                const left = Math.min(start.left, end.left)
                const top = Math.min(start.top, end.top)
                const right = Math.max(start.right, end.right)
                const bottom = Math.max(start.bottom, end.bottom)
                return new DOMRect(left, top, right - left, bottom - top)
              } catch {
                return null
              }
            }}
            onSelectionUpdate={(cb) => {
              if (!editor) return () => {}
              editor.on('selectionUpdate', cb)
              editor.on('focus', cb)
              editor.on('blur', cb)
              return () => {
                editor.off('selectionUpdate', cb)
                editor.off('focus', cb)
                editor.off('blur', cb)
              }
            }}
          />
        )}
      </div>

      <div className="px-4 py-2 flex items-center justify-between border-t">
        <div className="flex items-center gap-2">
          {canWrite ? <SaveStatusIndicator status={saveStatus} retriesExhausted={retriesExhausted} onRetry={handleRetrySave} /> : <div />}
          {activeEditors.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-meta">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-warning))]" />
              {activeEditors.length === 1
                ? `${activeEditors[0]?.fullName ?? 'Someone'} is editing`
                : `${activeEditors.length} people are editing`}
            </span>
          )}
        </div>
        <DraftStatsFooter
          words={wordCountDisplay}
          wordsClassName={getWordCountColor(wordCount, targetWordCount)}
          toDos={footerStats?.toDos}
          sources={footerStats?.sources}
        />
      </div>

      <SegmentConflictDialog
        open={conflict !== null}
        editorName={conflict?.editorName ?? null}
        isSelf={conflict?.isSelf ?? false}
        onKeepMine={() => void handleConflictKeepMine()}
        onLoadTheirs={() => void handleConflictLoadTheirs()}
      />

      {currentSessionId && (
        <VersionHistoryDialog
          open={versionHistoryOpen}
          onOpenChange={setVersionHistoryOpen}
          sessionId={currentSessionId}
          sectionId={versionHistoryTarget?.sectionId ?? persistedSectionId}
          sectionHeading={isCompositeDocument ? (versionHistoryTarget?.heading ?? null) : null}
          currentUserId={currentUserId}
          canWrite={canWrite}
          onRestored={handleRestored}
        />
      )}

      <ExportGapConfirmDialog
        open={exportGapScan !== null}
        sections={
          exportGapScan
            ? [
                {
                  sectionKey,
                  title: section.title,
                  todos: exportGapScan.todos,
                  malformedCount: exportGapScan.malformedCount,
                },
              ]
            : []
        }
        attachmentCount={exportGapScan?.attachmentCount ?? 0}
        onOpenChange={(open) => {
          if (!open) setExportGapScan(null)
        }}
        onExportAnyway={() => {
          setExportGapScan(null)
          void runExportSection()
        }}
        onReviewGaps={() => {
          setExportGapScan(null)
          jumpToFirstGap()
        }}
      />
    </div>
  )
}
